import { CHECKLIST_OPERATOR_OPTIONS, CONTAIN_OPERATOR_OPTIONS, EXIST_TYPE_OPTION, EMPTY_TYPE_OPTIONS, ENRICHMENT_OPTIONS, EQUAL_OPERATOR_OPTIONS, NUMBER_OPERATOR_OPTIONS } from '.';
import { BIGQUERY_FIELD_TYPE, SCOPED } from './analytics';
import { CLICK_ID_DEFAULT } from './dimension';

export const SEGMENT_OBJECT = {
     EVENT: 'event',
     SESSION: 'session',
     USER: 'user',
     PEOPLE: 'people',
     COMPANY: 'revealed-company',
     WEB_CONVERSION: 'web-conversion',
     CRM_LEAD: 'crm-lead',
     CRM_CONTACT: 'crm-contact',
     CRM_ACCOUNT: 'crm-account',
     CRM_PARENT_ACCOUNT: 'crm-parent-account',
     CRM_OPPORTUNITY: 'crm-opportunity',
     CRM_QUOTE: 'crm-quote',
     CRM_ORDER: 'crm-order',
     CRM_CONTRACT: 'crm-contract',
     CRM_CAMPAIGN: 'crm-campaign',
     CRM_EVENT: 'crm-event',
};

export const LL_SEGMENT_OBJECTS = [SEGMENT_OBJECT.EVENT, SEGMENT_OBJECT.SESSION, SEGMENT_OBJECT.USER, SEGMENT_OBJECT.PEOPLE, SEGMENT_OBJECT.COMPANY, SEGMENT_OBJECT.WEB_CONVERSION];

export const VARIABLE_MAIN_OPERATOR = [
     { value: 'exclude', label: 'Exclude' },
     { value: 'include', label: 'Include' },
];

export const SEGMENT_OBJECT_OPTIONS = [
     // { value: SEGMENT_OBJECT.EVENT, label: 'Event' },
     { value: SEGMENT_OBJECT.SESSION, label: 'Session' },
     { value: SEGMENT_OBJECT.USER, label: 'User' },
     { value: SEGMENT_OBJECT.PEOPLE, label: 'People' },
     { value: SEGMENT_OBJECT.COMPANY, label: 'Revealed Company' },
     { value: SEGMENT_OBJECT.WEB_CONVERSION, label: 'Web Conversion Event' },
];

// Entitlement only -- whether the Event object is offered in the UI at all. Keep in sync with
// EVENT_SEGMENT_ACCOUNT_IDS in server/models/query-builders/common/segment/eventScope.js, which
// controls a different thing (every segment table an account builds is forced to event grain) and
// has no validation tying it back to this list. Nothing rejects a mismatch:
//   - account here but not there: an Event-scoped rule group still builds correctly (grain follows
//     the rule group itself), but the account's OTHER segments silently stay session-grain, so
//     embedding one of those into the Event one breaks again -- the exact bug this account-wide
//     forcing exists to prevent.
//   - account there but not here: the Event object never appears in the UI, but every segment the
//     account has silently costs more to build (event-grain row volume), with nothing surfacing why.
export const EVENT_SEGMENT_ACCOUNT_IDS = [
     '390c8686-7d8d-4d5d-b7ff-f8a8ec9277e7', // Virtual Underwriter
];

const NORMALIZED_EVENT_SEGMENT_ACCOUNT_IDS = EVENT_SEGMENT_ACCOUNT_IDS.map((accountId) => accountId.toLowerCase());

export const isEventSegmentEnabled = (accountId) =>
     !!accountId && NORMALIZED_EVENT_SEGMENT_ACCOUNT_IDS.includes(String(accountId).trim().toLowerCase());

export const MAPPING_SF_SEGMENT_OBJECT = {
     Lead: SEGMENT_OBJECT.CRM_LEAD,
     Contact: SEGMENT_OBJECT.CRM_CONTACT,
     Account: SEGMENT_OBJECT.CRM_ACCOUNT,
     Opportunity: SEGMENT_OBJECT.CRM_OPPORTUNITY,
     Quote: SEGMENT_OBJECT.CRM_QUOTE,
     Order: SEGMENT_OBJECT.CRM_ORDER,
     Contract: SEGMENT_OBJECT.CRM_CONTRACT,
     Campaign: SEGMENT_OBJECT.CRM_CAMPAIGN,
     Event: SEGMENT_OBJECT.CRM_EVENT,
};

export const MAPPING_SEGMENT_OBJECT_SCOPE = {
     [SEGMENT_OBJECT.EVENT]: SCOPED.EVENT,
     [SEGMENT_OBJECT.SESSION]: SCOPED.SESSION,
     [SEGMENT_OBJECT.USER]: SCOPED.USER,
     [SEGMENT_OBJECT.PEOPLE]: SCOPED.PEOPLE,
     [SEGMENT_OBJECT.COMPANY]: SCOPED.COMPANY,
     [SEGMENT_OBJECT.WEB_CONVERSION]: SCOPED.CONVERSION,
     [SEGMENT_OBJECT.CRM_LEAD]: SCOPED.SF_LEAD,
     [SEGMENT_OBJECT.CRM_CONTACT]: SCOPED.SF_CONTACT,
     [SEGMENT_OBJECT.CRM_ACCOUNT]: SCOPED.SF_ACCOUNT,
     [SEGMENT_OBJECT.CRM_OPPORTUNITY]: SCOPED.SF_OPPORTUNITY,
     [SEGMENT_OBJECT.CRM_QUOTE]: SCOPED.SF_QUOTE,
     [SEGMENT_OBJECT.CRM_ORDER]: SCOPED.SF_ORDER,
     [SEGMENT_OBJECT.CRM_CONTRACT]: SCOPED.SF_CONTRACT,
     [SEGMENT_OBJECT.CRM_CAMPAIGN]: SCOPED.SF_CAMPAIGN,
     [SEGMENT_OBJECT.CRM_EVENT]: SCOPED.SF_EVENT,
};

export const MAPPING_SEGMENT_OBJECT_CRM_TABLE = {
     [SEGMENT_OBJECT.CRM_LEAD]: 'CRMLeads',
     [SEGMENT_OBJECT.CRM_CONTACT]: 'CRMContacts',
     [SEGMENT_OBJECT.CRM_ACCOUNT]: 'CRMAccounts',
     [SEGMENT_OBJECT.CRM_OPPORTUNITY]: 'CRMOpportunities',
     [SEGMENT_OBJECT.CRM_QUOTE]: 'CRMQuotes',
     [SEGMENT_OBJECT.CRM_ORDER]: 'CRMOrders',
     [SEGMENT_OBJECT.CRM_CONTRACT]: 'CRMContracts',
     [SEGMENT_OBJECT.CRM_CAMPAIGN]: 'CRMCampaigns',
     [SEGMENT_OBJECT.CRM_EVENT]: 'CRMActivitiesEvents',
};

export const CONDITION_FOCUS = {
     PROPERTY: 'property',
     METRIC: 'metric',
     WEB_CONVERSION_EVENT: 'web-conversion-event',
     ECOMMERCE_EVENT: 'ecommerce-event',
     RELATION_SHIP: 'relationship',
     OTHER_EVENT: 'other-event',
     SALES_CONVERSION_EVENT: 'sales-conversion-event',
};

export const CONDITION_FOCUS_OPTIONS = [
     { value: CONDITION_FOCUS.PROPERTY, label: 'Property' },
     { value: CONDITION_FOCUS.METRIC, label: 'Metric' },
     { value: CONDITION_FOCUS.WEB_CONVERSION_EVENT, label: 'Web Conversion Event' },
     { value: CONDITION_FOCUS.ECOMMERCE_EVENT, label: 'Ecommerce Event' },
     { value: CONDITION_FOCUS.RELATION_SHIP, label: 'Relationship' },
     { value: CONDITION_FOCUS.OTHER_EVENT, label: 'Other Event' },
     { value: CONDITION_FOCUS.SALES_CONVERSION_EVENT, label: 'Sales Conversion Event' },
];

export const SEGMENT_VARIABLE_OPTION = {
     CHECKLIST: 'checklist',
     OTHER_FIELD: 'otherField',
     INTENT: 'intent',
     METRIC: 'metric',
     CONVERSION: 'conversion',
     ECOMMERCE: 'eCommerce',
     EVENT_NAME: 'eventName',
     EVENT_VARIABLE: 'eventVariable',
     SALES_CONVERSION: 'salesConversion',
     PIPELINE_STATUS: 'pipelineStatus',
     DATA_ACTION_OTHER_FIELD: 'dataActionOtherField',
};

export const CONDITION_FOCUS_OPTION_VARIABLES = {
     [CONDITION_FOCUS.PROPERTY]: [SEGMENT_VARIABLE_OPTION.CHECKLIST, SEGMENT_VARIABLE_OPTION.OTHER_FIELD, SEGMENT_VARIABLE_OPTION.INTENT, SEGMENT_VARIABLE_OPTION.PIPELINE_STATUS],
     [CONDITION_FOCUS.METRIC]: [SEGMENT_VARIABLE_OPTION.METRIC, SEGMENT_VARIABLE_OPTION.OTHER_FIELD],
     [CONDITION_FOCUS.WEB_CONVERSION_EVENT]: [SEGMENT_VARIABLE_OPTION.CONVERSION, SEGMENT_VARIABLE_OPTION.CHECKLIST],
     [CONDITION_FOCUS.ECOMMERCE_EVENT]: [SEGMENT_VARIABLE_OPTION.ECOMMERCE],
     [CONDITION_FOCUS.OTHER_EVENT]: [SEGMENT_VARIABLE_OPTION.EVENT_NAME, SEGMENT_VARIABLE_OPTION.EVENT_VARIABLE],
     [CONDITION_FOCUS.SALES_CONVERSION_EVENT]: [SEGMENT_VARIABLE_OPTION.SALES_CONVERSION],
};

export const DATE_SEGMENT_CONDITION_OPTIONS = [
     { value: 'on', label: 'is on' },
     { value: 'bf', label: 'is before' },
     { value: 'af', label: 'is after' },
     { value: 'prenod', label: 'is in some previous number of days' },
];

export const VARIABLE_CONDITION_RELATIONSHIP_SEGMENT = [
     { value: 'gt', label: 'more than' },
     { value: 'lt', label: 'less than' },
     { value: 'eq', label: 'equals to' },
     { value: 'gtoet', label: 'greater than or equal to' },
     { value: 'ltoet', label: 'less than or equal to' },
     { value: 'exav', label: 'exists' },
     { value: 'nex', label: 'does not exist' },
];

export const SEGMENT_FIELD_TYPE = {
     DIMENSION: 'dimension',
     METRIC: 'metric',
     CUSTOM: 'custom',
     CONVERSION: 'conversion',
     SALES_CONVERSION: 'sales-conversion',
     ECOMMERCE: 'ecommerce',
     TRIGGER: 'trigger',
     CRM_LIFETIME_COUNT: 'crm-lifetime-count',
     DATE: 'date',
     MULTI_VALUE: 'multi-value',
     BOOLEAN: 'boolean',
     NUMBER: 'number',
     INTENT: 'intent',
};

export const SEGMENT_CONDITION_TYPE = {
     EVENT_COUNT: 'event-count',
     EVENT_STATUS: 'event-status',
     HAS_PROPERTY: 'has-property',
     EVENT: 'event',
     VARIABLE: 'variable',
};

export const SEGMENT_EVENT_CONDITION_TYPE_OPTIONS = [
     { value: SEGMENT_CONDITION_TYPE.EVENT_COUNT, label: 'Event count is' },
     { value: SEGMENT_CONDITION_TYPE.HAS_PROPERTY, label: 'Has property' },
];

export const DATE_RANGE_FILTER_TYPE = {
     LIFETIME_OF_THE_OBJECT: 'lifetime-of-the-object',
     PREVIOUS_PERIOD: 'previous-period',
     SPECIFIC_DATE_RANGE: 'specific-date-range',
};

export const VARIABLE_DATE_RANGE_FILTER_TYPE_SEGMENT = [
     { value: DATE_RANGE_FILTER_TYPE.LIFETIME_OF_THE_OBJECT, label: 'Lifetime of the object' },
     { value: DATE_RANGE_FILTER_TYPE.PREVIOUS_PERIOD, label: 'Previous period' },
     { value: DATE_RANGE_FILTER_TYPE.SPECIFIC_DATE_RANGE, label: 'Specific date range' },
];

export const VARIABLE_TYPE_OTHER_EVENT_SEGMENT = [
     { value: 'event', label: 'Event' },
     { value: 'variable', label: 'Variable' },
];

export const VARIABLE_CONDITION_OTHER_EVENT_SEGMENT = [
     { value: 'ct', label: 'contains' },
     { value: 'nct', label: 'not contain' },
     { value: 'eq', label: 'equals to' },
     { value: 'neq', label: 'not equal to' },
     { value: 'exav', label: 'exists' },
     { value: 'nex', label: 'does not exist' },
];

export const BOOLEAN_OPTIONS = [
     { value: 'true', label: 'True' },
     { value: 'false', label: 'False' },
];

export const YES_NO_OPTIONS = [
     { value: 'Yes', label: 'Yes' },
     { value: 'No', label: 'No' },
];

export const VARIABLE_CONDITION_DATE = [
     { value: 'on', label: 'on' },
     { value: 'bf', label: 'before' },
     { value: 'af', label: 'after' },
];

export const VARIABLE_LIST_MONTH = [
     { value: 'January', label: 'January' },
     { value: 'February', label: 'February' },
     { value: 'March', label: 'March' },
     { value: 'April', label: 'April' },
     { value: 'May', label: 'May' },
     { value: 'June', label: 'June' },
     { value: 'July', label: 'July' },
     { value: 'August', label: 'August' },
     { value: 'September', label: 'September' },
     { value: 'October', label: 'October' },
     { value: 'November', label: 'November' },
     { value: 'December', label: 'December' },
];

export const VARIABLE_LIST_DAY_OF_WEEK = [
     { value: 'Sunday', label: 'Sunday' },
     { value: 'Monday', label: 'Monday' },
     { value: 'Tuesday', label: 'Tuesday' },
     { value: 'Wednesday', label: 'Wednesday' },
     { value: 'Thursday', label: 'Thursday' },
     { value: 'Friday', label: 'Friday' },
     { value: 'Saturday', label: 'Saturday' },
];

export const VARIABLE_LIST_HOUR = [
     { value: '1 AM', label: '1 AM' },
     { value: '2 AM', label: '2 AM' },
     { value: '3 AM', label: '3 AM' },
     { value: '4 AM', label: '4 AM' },
     { value: '5 AM', label: '5 AM' },
     { value: '6 AM', label: '6 AM' },
     { value: '7 AM', label: '7 AM' },
     { value: '8 AM', label: '8 AM' },
     { value: '9 AM', label: '9 AM' },
     { value: '10 AM', label: '10 AM' },
     { value: '11 AM', label: '11 AM' },
     { value: '12 AM', label: '12 AM' },
     { value: '1 PM', label: '1 PM' },
     { value: '2 PM', label: '2 PM' },
     { value: '3 PM', label: '3 PM' },
     { value: '4 PM', label: '4 PM' },
     { value: '5 PM', label: '5 PM' },
     { value: '6 PM', label: '6 PM' },
     { value: '7 PM', label: '7 PM' },
     { value: '8 PM', label: '8 PM' },
     { value: '9 PM', label: '9 PM' },
     { value: '10 PM', label: '10 PM' },
     { value: '11 PM', label: '11 PM' },
     { value: '12 PM', label: '12 PM' },
];

export const VARIABLE_LIST_QUARTER_OF_THE_YEAR = [
     { value: 'Q1', label: 'Q1' },
     { value: 'Q2', label: 'Q2' },
     { value: 'Q3', label: 'Q3' },
     { value: 'Q4', label: 'Q4' },
];

export const VARIABLE_VALUE_CONVERSION_TYPE = [
     { value: 'secondary', label: 'Secondary' },
     { value: 'primary', label: 'Primary' },
     { value: 'None', label: 'None' },
];

export const SECTION_OPTION_SEGMENT = {
     SYSTEM_FIELDS: 'System Fields',
     IDENTIFIER_RELATIONSHIPS: 'Identifiers & Relationships',
     DATE: 'Date',
     TIMESTAMPS: 'Timestamps',
     CONVERSIONS: 'Conversions',
     CONVERSION_SOURCE_INFO: 'Conversion Source Info',
     ECOMMERCE_EVENT_DETAILS: 'Ecommerce Event Details',
     ECOMMERCE_ITEMS: 'Ecommerce Items',
     ECOMMERCE_SOURCE_INFO: 'Ecommerce Source Info',
     GEOGRAPHY: 'Geography',
     CONSENT: 'Consent',
     DEVICE: 'Device',
     BROWSER: 'Browser',
     OS: 'OS',
     SCREEN: 'Screen',
     ENGAGEMENT: 'Engagement',
     FORM_ACTIVITY: 'Form Activity',
     SOURCE: 'Source',
     PAGE_ACTIVITY: 'Page Activity',
     PEOPLE: 'People',
     ECOMMERCE: 'Ecommerce',
     TRIGGER: 'Triggers',
     COMPANY_DETAILS: 'Company Details',
     LIFETIME_SOURCE_SUMMARY: 'Lifetime Source Summary',
     USER_SOURCE: 'userSource',
     CHANNEL: 'Channel',
     CLICK_IDS: 'Click IDs',
     REFERRER: 'Referrer',
     UTM: 'UTM',
     CREATED_USER_SOURCE: 'Created userSource',
     ENGAGEMENT_ACTIVITY: 'Engagement & Activity',
     PERSONAL_DATA: 'Personal Data',
     PERSON_CREATED_SOURCE_INFO: 'Person Created Source Info',
     CUSTOM_PROPERTIES: 'Custom Properties',
     LOCATION: 'Location',
     SIZE: 'Size',
     INDUSTRY: 'Industry',
     REVEALED_SOURCE_SUMMARY: 'Revealed Source Summary',
     TRACKING_LEVEL: 'Tracking Level',
     EVENT_DATA: 'Event Data',
     GENERAL_INFORMATION: 'General Information',
     CREATED_SESSION: 'Created Session',
     CREATED_SOURCE: 'Created Source',
     CREATED_SOURCE_REFERRER: 'Created Source Referrer',
     CREATED_LOCATION: 'Created Location',
     CREATED_UTM: 'Created UTM',
     CREATED_CLICK_IDS: 'Created ClickIDs',
     CREATED_CHANNEL: 'Created Channel',
     SESSION: 'Session',
     TARGET_ACCOUNT: 'Target Account',
     DATE_ACCOUNT: 'Date (Account)',
     RECORD_INFO: 'Record Info',
     OTHER_FIELDS: 'Other Fields',
     PIPELINE_STATUS: 'Pipeline & Status',
     VALUES: 'Values',
     TARGETING_FIELDS: 'Targeting Fields',
     SOURCES_JOURNEY: 'Sources & Journey',
     CHANNELS_JOURNEY: 'Channels & Journey',
     CREATED_BY: 'Created by',
     LIFETIME: 'Lifetime',
     IMPORTED: 'Imported',
     CLOSED: 'Closed',
     LISTENLAYER_OBJECTS: 'ListenLayer Objects',
     EXTERNAL_OBJECTS: 'External Objects',
     LIFETIME_SOURCE_DETAILS: 'Lifetime Source Details',
     WEB_CONVERSIONS: 'Web Conversions',
     SALES_CONVERSIONS: 'Sales Conversions',
     INTENT_PROPERTIES: 'Intent Properties',
     COMPANIES: 'Companies',
     REVEALED_SOURCE: 'Revealed Sources',
     SALE_CONVERSIONS: 'Sales Conversions',
};

export const DATA_SOURCE_OPTIONS = [
     { label: 'Revealed', value: 'Revealed' },
     { label: 'Provided', value: 'Provided' },
     { label: 'Imported', value: 'Imported' },
];

export const EMAIL_TYPE_OPTIONS = [
     { value: 'business', label: 'Business' },
     { value: 'personal', label: 'Personal' },
     { value: 'education', label: 'Education' },
     { value: 'None', label: 'None' },
];

// CRM Properties
const CRM_DATE_ACCOUNT_PROPERTY_OPTIONS = [
     { value: 'createdDate', label: 'Created Date' },
     { value: 'lastModifiedAt', label: 'Last Modified' },
     { value: 'firstSessionAt', label: 'First Session Date' },
     { value: 'lastSessionAt', label: 'Last Session Date' },
].map((item) => ({ ...item, type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT }));

const CRM_VALUE_PROPERTY_OPTIONS = [
     { value: 'crmMRRCurrency', label: 'MRRCurrency' },
     { value: 'crmQRRCurrency', label: 'QRRCurrency' },
     { value: 'crmARRCurrency', label: 'ARRCurrency' },
     { value: 'crmTCVCurrency', label: 'TCVCurrency' },
     { value: 'crmPRJCurrency', label: 'PRJCurrency' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES }));

const CRM_CREATED_SOURCE_PROPERTY_OPTIONS = [
     { value: 'createdSourceFirst', label: 'Created Source (first)' },
     { value: 'createdSourceLast', label: 'Created Source (last)' },
     { value: 'createdSourcePath', label: 'Created Source (path)' },
     { value: 'createdSourcePathND', label: 'Created Source (path) Nondirect' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY }));

const CRM_CLOSED_SOURCE_PROPERTY_OPTIONS = [
     { value: 'closedSourceLast', label: 'Closed Source (last)' },
     { value: 'closedSourcePath', label: 'Closed Source (path)' },
     { value: 'closedSourcePathND', label: 'Closed Source (path) Nondirect' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY }));

const CRM_CREATED_CHANNEL_PROPERTY_OPTIONS = [
     { value: 'createdSourceChannel[first]', label: 'Created Channel (first)' },
     { value: 'createdSourceChannel[last]', label: 'Created Channel (last)' },
     { value: 'createdSourceChannel[path]', label: 'Created Channel (path)' },
     { value: 'createdSourceChannel[pathND]', label: 'Created Channel (path) Nondirect' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.CHANNELS_JOURNEY }));

const CRM_CREATED_UTM_PROPERTY_OPTIONS = ['utm_source', 'utm_medium', 'utm_source_medium', 'utm_campaign', 'utm_content', 'utm_term']
     .flatMap((name) => ['path'].map((type) => ({ name, type })))
     .map((item) => {
          const labelName = item.name === 'utm_source_medium' ? 'utm_source / utm_medium' : item.name;
          return {
               value: `createdSources.utms.${item.name}[${item.type}]`,
               label: `Created by ${labelName} (${item.type})`,
               section: SECTION_OPTION_SEGMENT.CREATED_BY,
               sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY,
          };
     });

const CRM_CLICK_IDS = ['gclid', 'fbclid', '_fbp', '_fbc', 'msclkid', 'li_fat_id', 'ttclid', 'dclid', 'srsltid', '_ga', 'calltrk_user'];

const CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS = CRM_CLICK_IDS.flatMap((name) => ['first', 'last', 'path'].map((type) => ({ name, type }))).map((item) => ({
     value: `createdSources.clickIDs.${item.name}[${item.type}]`,
     label: `Created by ${item.name} (${item.type})`,
     section: SECTION_OPTION_SEGMENT.CREATED_BY,
     sectionParent: SECTION_OPTION_SEGMENT.CLICK_IDS,
}));

const CRM_LIFETIME_CLICK_ID_PATH_PROPERTY_OPTIONS = CRM_CLICK_IDS.flatMap((name) => ['path'].map((type) => ({ name, type }))).map((item) => ({
     value: `sources.clickIDs.${item.name}[${item.type}]`,
     label: `Lifetime by ${item.name} (${item.type})`,
     section: SECTION_OPTION_SEGMENT.LIFETIME,
     sectionParent: SECTION_OPTION_SEGMENT.CLICK_IDS,
}));

const CRM_IMPORTED_CLICK_ID_PROPERTY_OPTIONS = CLICK_ID_DEFAULT.flatMap((name) => ['path'].map((type) => ({ name, type }))).map((item) => ({
     value: `crmClickIDs.${item.name}[${item.type}]`,
     label: `Imported ${item.name} (${item.type})`,
     section: SECTION_OPTION_SEGMENT.IMPORTED,
     sectionParent: SECTION_OPTION_SEGMENT.CLICK_IDS,
}));

// CRM Metrics
const CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS = [
     { value: 'sessionCount', label: 'Sessions (lifetime)' },
     { value: 'createdSessionCount', label: 'Sessions (created by)' },
     { value: 'userCount', label: 'Users (lifetime)' },
     { value: 'peopleCount', label: 'People (lifetime)' },
     { value: 'companyCount', label: 'Companies (lifetime)' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS }));

const CRM_ENGAGEMENT_METRIC_OPTIONS = [
     { value: 'engagedSessionCount', label: 'Engaged Sessions (lifetime)' },
     { value: 'engagementScore', label: 'Engagement Score (lifetime)' },
     { value: 'avgSessionDuration', label: 'Avg. Session Duration (lifetime)' },
     { value: 'pageViewCount', label: 'Pageviews (lifetime)' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT }));

const CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS = [
     { value: 'conversionCount', label: 'All Web Conversions (lifetime)' },
     { value: 'primaryConversionCount', label: 'Primary Web Conversions (lifetime)' },
     { value: 'secondaryConversionCount', label: 'Secondary Web Conversions (lifetime)' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS }));

const CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS = [
     { value: 'salesConversionCount', label: 'All Sales Conversions (lifetime)' },
     { value: 'primarySalesConversionCount', label: 'Primary Sales Conversions (lifetime)' },
     { value: 'secondarySalesConversionCount', label: 'Secondary Sales Conversions (lifetime)' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS }));

const CRM_VALUE_METRIC_OPTIONS = [
     { value: 'crmMRRAmount', label: 'MRRAmount' },
     { value: 'crmMRRQuantity', label: 'MRRQuantity' },
     { value: 'crmMRRProbability', label: 'MRRProbability' },
     { value: 'crmQRRAmount', label: 'QRRAmount' },
     { value: 'crmQRRQuantity', label: 'QRRQuantity' },
     { value: 'crmQRRProbability', label: 'QRRProbability' },
     { value: 'crmARRAmount', label: 'ARRAmount' },
     { value: 'crmARRQuantity', label: 'ARRQuantity' },
     { value: 'crmARRProbability', label: 'ARRProbability' },
     { value: 'crmTCVAmount', label: 'TCVAmount' },
     { value: 'crmTCVQuantity', label: 'TCVQuantity' },
     { value: 'crmTCVProbability', label: 'TCVProbability' },
     { value: 'crmPRJAmount', label: 'PRJAmount' },
     { value: 'crmPRJQuantity', label: 'PRJQuantity' },
     { value: 'crmPRJProbability', label: 'PRJProbability' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES }));

const EMAIL_SUB_OPTIONS = [
     { value: 'email', label: 'Email', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS] },
     {
          value: 'valid',
          label: 'Valid',
          type: SEGMENT_FIELD_TYPE.MULTI_VALUE,
          conditions: [],
          valueOptions: [
               { value: 'valid', label: 'Valid' },
               { value: 'invalid', label: 'Invalid' },
               { value: 'unknown', label: 'Unknown' },
          ],
     },
     { value: 'qualityScore', label: 'Quality Score', fieldType: BIGQUERY_FIELD_TYPE.FLOAT, conditions: NUMBER_OPERATOR_OPTIONS },
     {
          value: 'emailType',
          label: 'Email Type',
          type: SEGMENT_FIELD_TYPE.MULTI_VALUE,
          conditions: [],
          valueOptions: [
               { value: 'personal', label: 'Personal' },
               { value: 'business', label: 'Business' },
               { value: 'education', label: 'Education' },
          ],
     },
     { value: 'roleEmail', label: 'Role Email', type: SEGMENT_FIELD_TYPE.BOOLEAN },
     { value: 'catchAllEmail', label: 'Catch All Email', type: SEGMENT_FIELD_TYPE.BOOLEAN },
     { value: 'disposableEmail', label: 'Disposable Email', type: SEGMENT_FIELD_TYPE.BOOLEAN },
];

const COMPANY_SUB_OPTIONS = [
     { value: 'companyName', label: 'Company Name', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS] },
     { value: 'domain', label: 'Domain', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS] },
     {
          value: 'primaryIndustry',
          label: 'Primary Industry',
          conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...CHECKLIST_OPERATOR_OPTIONS],
     },
     { value: 'revenue', label: 'Revenue', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...CHECKLIST_OPERATOR_OPTIONS] },
     { value: 'primary', label: 'Primary', type: SEGMENT_FIELD_TYPE.BOOLEAN },
     { value: 'currentB2BRole', label: 'Current B2B Role', type: SEGMENT_FIELD_TYPE.BOOLEAN },
];

const OTHER_ROLES_SUB_OPTIONS = [
     { value: 'companyName', label: 'Company Name', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'domain', label: 'Domain', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'companyLinkedinUrl', label: 'Company Linkedin URL', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'title', label: 'Title', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'currentRole', label: 'Current Role' , type: SEGMENT_FIELD_TYPE.BOOLEAN },
     { value: 'seniority', label: 'Seniority', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'jobFunctions', label: 'Job Functions', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'employmentType', label: 'Employment Type', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'startDate', label: 'Start Date', conditions: [...DATE_SEGMENT_CONDITION_OPTIONS, ...EXIST_TYPE_OPTION], type: SEGMENT_FIELD_TYPE.DATE },
     { value: 'endDate', label: 'End Date', conditions: [...DATE_SEGMENT_CONDITION_OPTIONS, ...EXIST_TYPE_OPTION], type: SEGMENT_FIELD_TYPE.DATE },
];

const VOLUNTEERING_SUB_OPTIONS = [
     { value: 'companyName', label: 'Company Name', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'role', label: 'Role', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'summary', label: 'Summary', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'currentRole', label: 'Current Role', type: SEGMENT_FIELD_TYPE.BOOLEAN },
     { value: 'startDate', label: 'Start Date', conditions: [...DATE_SEGMENT_CONDITION_OPTIONS, ...EXIST_TYPE_OPTION], type: SEGMENT_FIELD_TYPE.DATE },
     { value: 'endDate', label: 'End Date', conditions: [...DATE_SEGMENT_CONDITION_OPTIONS, ...EXIST_TYPE_OPTION], type: SEGMENT_FIELD_TYPE.DATE },
];

const CERTIFICATIONS_SUB_OPTIONS = [
     { value: 'title', label: 'Title', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'summary', label: 'Summary', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'companyName', label: 'Company Name', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'month', label: 'Month', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'year', label: 'Year', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
];

const EDUCATION_SUB_OPTIONS = [
     { value: 'schoolName', label: 'School Name', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'fieldOfStudy', label: 'Field Of Study', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'degree', label: 'Degree', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'startYear', label: 'Start Year', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'endYear', label: 'End Year', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
];

const PROJECTS_SUB_OPTIONS = [
     { value: 'title', label: 'Title', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'summary', label: 'Summary', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'url', label: 'URL', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
];

const RECOMMENDATIONS_SUB_OPTIONS = [
     { value: 'recommenderName', label: 'Recommender Name', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
     { value: 'recommendation', label: 'Recommendation', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...EXIST_TYPE_OPTION] },
];

export const MAPPING_SUB_FIELD_SEGMENT = {
     enrichment: [
          { value: 'B2B', label: 'B2B' },
          { value: 'B2C', label: 'B2C' },
          { value: 'anyField', label: 'Any Sub Field' },
          { value: 'allField', label: 'All Sub Field' },
     ].map((item) => ({ ...item, conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...CHECKLIST_OPERATOR_OPTIONS] })),
     currentCompany: [
          { value: 'companyName', label: 'Company Name', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS] },
          { value: 'domain', label: 'Domain', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS] },
          {
               value: 'currentPrimaryIndustry',
               label: 'Current Primary Industry',
               conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...CHECKLIST_OPERATOR_OPTIONS],
          },
          { value: 'currentRevenue', label: 'Current Revenue', conditions: [...CONTAIN_OPERATOR_OPTIONS, ...EQUAL_OPERATOR_OPTIONS, ...CHECKLIST_OPERATOR_OPTIONS] },
          ...EMPTY_TYPE_OPTIONS,
     ],
     email: [...EMAIL_SUB_OPTIONS, ...EMPTY_TYPE_OPTIONS], // Primary email
     emailList: [
          // All Email Addresses
          {
               value: 'anyField',
               label: 'Any Email',
               subOptions: [
                    ...EMAIL_SUB_OPTIONS,
                    { value: 'primary', label: 'Primary', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'currentB2BRole', label: 'Current B2B Role', type: SEGMENT_FIELD_TYPE.BOOLEAN },
               ],
          },
          {
               value: 'allField',
               label: 'All Email',
               subOptions: EMAIL_SUB_OPTIONS,
          },
          ...EMPTY_TYPE_OPTIONS,
     ],
     relatedCompanies: [
          { value: 'anyField', label: 'Any Companies', subOptions: COMPANY_SUB_OPTIONS },
          { value: 'allField', label: 'All Companies', subOptions: COMPANY_SUB_OPTIONS.filter((item) => !['primary', 'currentB2BRole'].includes(item.value)) },
          ...EMPTY_TYPE_OPTIONS,
     ],
     otherRoles: [
          { value: 'anyField', label: 'Any Other Roles', subOptions: OTHER_ROLES_SUB_OPTIONS },
          { value: 'allField', label: 'All Other Roles', subOptions: OTHER_ROLES_SUB_OPTIONS },
          ...EMPTY_TYPE_OPTIONS,
     ],
     volunteering: [
          { value: 'anyField', label: 'Any Volunteering', subOptions: VOLUNTEERING_SUB_OPTIONS },
          { value: 'allField', label: 'All Volunteering', subOptions: VOLUNTEERING_SUB_OPTIONS },
          ...EMPTY_TYPE_OPTIONS,
     ],
     certifications: [
          { value: 'anyField', label: 'Any Certifications', subOptions: CERTIFICATIONS_SUB_OPTIONS },
          { value: 'allField', label: 'All Certifications', subOptions: CERTIFICATIONS_SUB_OPTIONS },
          ...EMPTY_TYPE_OPTIONS,
     ],
     education: [
          { value: 'anyField', label: 'Any Education', subOptions: EDUCATION_SUB_OPTIONS },
          { value: 'allField', label: 'All Education', subOptions: EDUCATION_SUB_OPTIONS },
          ...EMPTY_TYPE_OPTIONS,
     ],
     projects: [
          { value: 'anyField', label: 'Any Projects', subOptions: PROJECTS_SUB_OPTIONS },
          { value: 'allField', label: 'All Projects', subOptions: PROJECTS_SUB_OPTIONS },
          ...EMPTY_TYPE_OPTIONS,
     ],
     recommendations: [
          { value: 'anyField', label: 'Any Recommendations', subOptions: RECOMMENDATIONS_SUB_OPTIONS },
          { value: 'allField', label: 'All Recommendations', subOptions: RECOMMENDATIONS_SUB_OPTIONS },
          ...EMPTY_TYPE_OPTIONS,
     ],
};

// Property & Metric
export const PROPERTY_METRIC_SEGMENT_OPTIONS = {
     [SEGMENT_OBJECT.EVENT]: {
          property: [
               { value: 'eventName', label: 'Event Name', section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'eventOriginal', label: 'Original Event Name', section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'eventPageLocation', label: 'Event Page Location', section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'eventHostnameLocation', label: 'Event Hostname', section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'creationDate', label: 'Creation Date', type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'isConversion', label: 'Is Conversion?', type: SEGMENT_FIELD_TYPE.BOOLEAN, section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'conversionType', label: 'Conversion Type', type: SEGMENT_FIELD_TYPE.MULTI_VALUE, section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'userId', label: 'User ID', section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'sessionId', label: 'Session ID', section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'personId', label: 'Person ID', section: SECTION_OPTION_SEGMENT.EVENT_DATA },
               { value: 'consentMethod', label: 'Consent Method', section: SECTION_OPTION_SEGMENT.TRACKING_LEVEL },
               { value: 'trackingLevel', label: 'Tracking Level', section: SECTION_OPTION_SEGMENT.TRACKING_LEVEL },
               { value: 'regionalConsentRule', label: 'Consent Regional Rule', section: SECTION_OPTION_SEGMENT.TRACKING_LEVEL },
               { value: 'adsStorage', label: 'ad_storage', type: SEGMENT_FIELD_TYPE.BOOLEAN, section: SECTION_OPTION_SEGMENT.TRACKING_LEVEL },
               { value: 'analyticsStorage', label: 'analytics_storage', type: SEGMENT_FIELD_TYPE.BOOLEAN, section: SECTION_OPTION_SEGMENT.TRACKING_LEVEL },
               { value: 'identifyStrength', label: 'Identify Strength', section: SECTION_OPTION_SEGMENT.TRACKING_LEVEL },
          ],
     },
     [SEGMENT_OBJECT.SESSION]: {
          property: [
               ...[
                    { value: 'creationDate', label: 'Creation Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'lastEventDate', label: 'Last Event Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'lastConversionDate', label: 'Last Conversion Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'hour', label: 'Hour', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'dayOfWeek', label: 'Day of Week', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'month', label: 'Month', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'yearMonth', label: 'Year Month' },
                    { value: 'quarter', label: 'Quarter', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'yearQuarter', label: 'Year Quarter' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE })),

               ...[
                    { value: 'landingPage', label: 'Landing Page' },
                    { value: 'exitPage', label: 'Exit Page' },
                    { value: 'landingPageURL', label: 'Landing Page URL' },
                    { value: 'landingPageHostname', label: 'Landing Page Hostname' },
                    { value: 'landingPagePath', label: 'Landing Page Path' },
                    { value: 'exitPageURL', label: 'Exit Page URL' },
                    { value: 'exitPageHostname', label: 'Exit Page Hostname' },
                    { value: 'exitPagePath', label: 'Exit Page Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PAGE_ACTIVITY })),

               ...[{ value: 'engaged', label: 'Engaged', type: SEGMENT_FIELD_TYPE.BOOLEAN }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),

               ...[
                    { value: 'sessionNumber', label: 'Session Number' },
                    { value: 'userID', label: 'User ID' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.IDENTIFIER_RELATIONSHIPS })),

               ...[
                    { value: 'consentMethod', label: 'Consent Method' },
                    { value: 'identifyStrength', label: 'Identify Strength' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CONSENT })),

               ...[
                    { value: 'city', label: 'City' },
                    { value: 'country', label: 'Country' },
                    { value: 'continent', label: 'Continent' },
                    { value: 'dmaCode', label: 'DMA Code' },
                    { value: 'dmaName', label: 'DMA Name' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.GEOGRAPHY })),

               ...[
                    { value: 'deviceType', label: 'Device Type' },
                    { value: 'deviceFamily', label: 'Device Family' },
                    { value: 'deviceManufacturer', label: 'Device Manufacturer' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DEVICE })),

               ...[
                    { value: 'browserFamily', label: 'Browser Family' },
                    { value: 'browserVersion', label: 'Browser Version' },
                    { value: 'browserDetailedVersion', label: 'Browser Detailed Version' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.BROWSER })),

               ...[
                    { value: 'osFamily', label: 'OS Family' },
                    { value: 'osVersion', label: 'OS Version' },
                    { value: 'osDetailedVersion', label: 'OS Detailed Version' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.OS })),

               ...[
                    { value: 'screenColors', label: 'Screen Colors' },
                    { value: 'screenSize', label: 'Screen Size' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SCREEN })),

               ...[{ value: 'userSource', label: 'User Source' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SOURCE })),

               ...[{ value: 'channel', label: 'Channel' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),

               ...[
                    { value: 'source.referrerHostname', label: 'Referrer Hostname' },
                    { value: 'source.referrerOriginPath', label: 'Referrer Origin Path' },
                    { value: 'source.referrerPath', label: 'Referrer Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.SOURCE })),

               ...[
                    { value: 'source.utm_medium', label: 'utm_medium' },
                    { value: 'source.utm_source', label: 'utm_source' },
                    { value: 'source.utm_campaign', label: 'utm_campaign' },
                    { value: 'source.utm_term', label: 'utm_term' },
                    { value: 'source.utm_content', label: 'utm_content' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.SOURCE })),

               ...[
                    { value: 'source.gclid', label: 'Click IDs (gclid)' },
                    { value: 'source.fbclid', label: 'Click IDs (fbclid)' },
                    { value: 'source._fbp', label: 'Click IDs (_fbp)' },
                    { value: 'source._fbc', label: 'Click IDs (_fbc)' },
                    { value: 'source.msclkid', label: 'Click IDs (msclkid)' },
                    { value: 'source.li_fat_id', label: 'Click IDs (li_fat_id)' },
                    { value: 'source.ttclid', label: 'Click IDs (ttclid)' },
                    { value: 'source.dclid', label: 'Click IDs (dclid)' },
                    { value: 'source.srsltid', label: 'Click IDs (srsltid)' },
                    { value: 'source._ga', label: 'Click IDs (_ga)' },
                    { value: 'source.calltrk_user', label: 'Click IDs (calltrk_user)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLICK_IDS, sectionParent: SECTION_OPTION_SEGMENT.SOURCE })),

               ...[
                    { value: 'forms.assignedID', label: 'Assigned ID' },
                    { value: 'forms.locationOriginPathname', label: 'Form Location' },
                    { value: 'forms.locationHostnamePath', label: 'Form Hostname Path' },
                    { value: 'forms.lastSubmissionDate', label: 'Last Submission Date', type: SEGMENT_FIELD_TYPE.DATE },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.FORM_ACTIVITY })),

               ...[{ value: 'personCreated', label: 'Person ID' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PEOPLE })),

               ...[
                    { value: 'isTargetAccount', label: 'Is Target Account', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'targetAccountDomain', label: 'Target Account Domain' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT })),
          ],
          metric: [
               ...[{ value: 'pageViewCount', label: 'Pageviews' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PAGE_ACTIVITY })),

               ...[{ value: 'personCreatedCount', label: 'Person Created' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PEOPLE })),

               ...[
                    { value: 'eventCount', label: 'Events' },
                    { value: 'engagementScore', label: 'Engagement Score' },
                    { value: 'sessionDuration', label: 'Session Duration' },
                    { value: 'avgVisibleTimePerPage', label: 'Avg Visible Time / Page' },
                    { value: 'visibleTime', label: 'Total Visible Time' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),

               ...[
                    { value: 'conversionCount', label: 'All Conversions' },
                    { value: 'primaryConversionCount', label: 'Primary Conversions' },
                    { value: 'secondaryConversionCount', label: 'Secondary Conversions' },
                    { value: 'conversionValue', label: 'Conversion Value' },
                    { value: 'primaryConvValue', label: 'Primary Conversion Value' },
                    { value: 'secondaryConvValue', label: 'Secondary Conversion Value' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),

               ...[
                    { value: 'ecomEventValue.addToCart', label: 'Add to Cart Value' },
                    { value: 'ecomEventValue.removeFromCart', label: 'Remove from Cart Value' },
                    { value: 'ecomEventValue.netCart', label: 'Net Cart Value' },
                    { value: 'ecomEventValue.beginCheckoutUnique', label: 'Begin Checkout Unique' },
                    { value: 'ecomEventRevenue.purchase', label: 'Purchase Revenue' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ECOMMERCE })),

               ...[
                    { value: 'forms.impressionCount', label: 'Impression Count' },
                    { value: 'forms.visibleCount', label: 'Form Visible Count' },
                    { value: 'forms.startCount', label: 'Form Start Count' },
                    { value: 'forms.errorCount', label: 'Form Error Count' },
                    { value: 'forms.submissions', label: 'Submissions Count' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.FORM_ACTIVITY })),
          ],
     },
     [SEGMENT_OBJECT.USER]: {
          property: [
               ...[
                    { value: 'userSourceFirst', label: 'User Source - First' },
                    { value: 'userSourcePath', label: 'User Source - Path' },
                    { value: 'noneDirectSourcePath', label: 'Non-Direct User Source - Path' },
                    { value: 'userSourceLast', label: 'User Source - Last' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.USER_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'channelFirst', label: 'Channel - First' },
                    { value: 'channelLast', label: 'Channel - Last' },
                    { value: 'channelPath', label: 'Channel - Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),

               ...[
                    { value: 'creationDate', label: 'Creation Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'createdHour', label: 'Creation Hour', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'createdDayOfWeek', label: 'Creation Day of Week', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'createdMonth', label: 'Creation Month', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'createdYearMonth', label: 'Creation Year Month' },
                    { value: 'createdQuarter', label: 'Creation Quarter', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'createdYearQuarter', label: 'Creation Year Quarter' },
                    { value: 'lastConversionDate', label: 'Last Conversion Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'lastEventDate', label: 'Last Event Date', type: SEGMENT_FIELD_TYPE.DATE },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE })),

               ...[
                    { value: 'userID', label: 'User ID' },
                    { value: 'personID', label: 'Person ID' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.IDENTIFIER_RELATIONSHIPS })),

               ...[{ value: 'consentMethod', label: 'Consent Method' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CONSENT })),

               ...[
                    { value: 'country', label: 'Country' },
                    { value: 'continent', label: 'Continent' },
                    { value: 'city', label: 'City' },
                    { value: 'dmaCode', label: 'DMA Code' },
                    { value: 'dmaName', label: 'DMA Name' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.GEOGRAPHY })),

               ...[
                    { value: 'deviceType', label: 'Device Type' },
                    { value: 'deviceFamily', label: 'Device Family' },
                    { value: 'deviceManufacturer', label: 'Device Manufacturer' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DEVICE })),

               ...[
                    { value: 'browserFamily', label: 'Browser Family' },
                    { value: 'browserVersion', label: 'Browser Version' },
                    { value: 'browserDetailedVersion', label: 'Browser Detailed Version' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.BROWSER })),

               ...[
                    { value: 'osFamily', label: 'OS Family' },
                    { value: 'osVersion', label: 'OS Version' },
                    { value: 'osDetailedVersion', label: 'OS Detailed Version' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.OS })),

               ...[
                    { value: 'screenColors', label: 'Screen Colors' },
                    { value: 'screenSize', label: 'Screen Size' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SCREEN })),

               ...[
                    { value: 'sessionIDFirst', label: 'Session ID (first)' },
                    { value: 'sessionIDFirstND', label: 'Session ID <br>(first non-direct)' },
                    { value: 'sessionIDLast', label: 'Session ID (last)' },
                    { value: 'sessionIDLastND', label: 'Session ID <br>(last non-direct)' },
                    { value: 'lifetimeSessionIDPath', label: 'Session ID (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SESSION, sectionParent: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'sources.referrerHostname', label: 'Referrer Hostname' },
                    { value: 'sources.referrerOriginPath', label: 'Referrer Origin Path' },
                    { value: 'sources.referrerPath', label: 'Referrer Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'sources.utm_medium', label: 'utm_medium' },
                    { value: 'sources.utm_source', label: 'utm_source' },
                    { value: 'sources.utm_campaign', label: 'utm_campaign' },
                    { value: 'sources.utm_term', label: 'utm_term' },
                    { value: 'sources.utm_content', label: 'utm_content' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'sources.gclid', label: 'Click IDs (gclid)' },
                    { value: 'sources.fbclid', label: 'Click IDs (fbclid)' },
                    { value: 'sources._fbp', label: 'Click IDs (_fbp)' },
                    { value: 'sources._fbc', label: 'Click IDs (_fbc)' },
                    { value: 'sources.msclkid', label: 'Click IDs (msclkid)' },
                    { value: 'sources.li_fat_id', label: 'Click IDs (li_fat_id)' },
                    { value: 'sources.ttclid', label: 'Click IDs (ttclid)' },
                    { value: 'sources.dclid', label: 'Click IDs (dclid)' },
                    { value: 'sources.srsltid', label: 'Click IDs (srsltid)' },
                    { value: 'sources._ga', label: 'Click IDs (_ga)' },
                    { value: 'sources.calltrk_user', label: 'Click IDs (calltrk_user)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLICK_IDS, sectionParent: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'isTargetAccount', label: 'Is Target Account', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'targetAccountDomain', label: 'Target Account Domain' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT })),
          ],
          metric: [
               ...[{ value: 'revealedCompanyCount', label: 'Revealed Company Count' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.COMPANY_DETAILS })),

               ...[
                    { value: 'eventCount', label: 'Events' },
                    { value: 'sessionCount', label: 'Sessions' },
                    { value: 'avgSessionDuration', label: 'Avg Session Duration' },
                    { value: 'engagedSessionCount', label: 'Engaged Sessions' },
                    { value: 'engagementRate', label: 'Engagement Rate' },
                    { value: 'engagementScore', label: 'Lifetime Engagement Score' },
                    { value: 'bouncedSessionCount', label: 'Bounced Sessions' },
                    { value: 'bounceRate', label: 'Bounce Rate' },
                    { value: 'pageViewCount', label: 'Pageviews' },
                    { value: 'avgPageViewsPerSession', label: 'Avg Pageviews / Session' },
                    { value: 'avgVisibleTimePerPage', label: 'Avg Visible Time / Page' },
                    { value: 'lifetimeVisibleTime', label: 'Total Visible Time <br>(Lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY })),

               ...[
                    { value: 'sourcePathCount', label: 'Source Path Count' },
                    { value: 'noneDirectSourcePathCount', label: 'Non-Direct Source Path Count' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'conversionCount', label: 'All Conversions' },
                    { value: 'primaryConversionCount', label: 'Primary Conversions' },
                    { value: 'secondaryConversionCount', label: 'Secondary Conversions' },
                    { value: 'conversionRateAll', label: 'Conv Rate <br>(All Conversions)' },
                    { value: 'conversionRatePrimary', label: 'Conv Rate <br>(Primary Conversions)' },
                    { value: 'conversionRateSecondary', label: 'Conv Rate <br>(Secondary Conversions)' },
                    { value: 'conversionValue', label: 'Conversion Value' },
                    { value: 'primaryConvValue', label: 'Primary Conversion Value <br>(Lifetime)' },
                    { value: 'secondaryConvValue', label: 'Secondary Conversion Value <br>(Lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
          ],
     },
     [SEGMENT_OBJECT.PEOPLE]: {
          property: [
               ...[
                    { value: 'email', label: 'Primary Email' },
                    { value: 'domain', label: 'Email Domain' },
                    { value: 'emailList', label: 'All Email Addresses' },
                    { value: 'emailType', label: 'Email Type' },
                    // {
                    //      value: 'validEmail',
                    //      label: 'Email Valid',
                    //      type: SEGMENT_FIELD_TYPE.MULTI_VALUE,
                    //      valueOptions: [
                    //           { value: 'Valid', label: 'Valid' },
                    //           { value: 'Invalid', label: 'Invalid' },
                    //           { value: 'Unknown', label: 'Unknown' },
                    //      ],
                    // },
                    { value: 'qualityScore', label: 'Email Quality Score', fieldType: BIGQUERY_FIELD_TYPE.FLOAT, conditions: NUMBER_OPERATOR_OPTIONS },
                    { value: 'externalId', label: 'External ID' },
                    { value: 'name', label: 'Full Name' },
                    { value: 'firstName', label: 'First Name' },
                    { value: 'lastName', label: 'Last Name' },
                    { value: 'phone', label: 'Primary Phone' },
                    { value: 'phoneList', label: 'Phone List' },
                    { value: 'dataSources', label: 'Data Sources' },
                    { value: 'revealedZipCode', label: 'Revealed Zip Code' },
                    { value: 'revealedCity', label: 'Revealed City' },
                    { value: 'revealedState', label: 'Revealed State' },
                    { value: 'enrichment', label: 'Enrichment', valueOptions: ENRICHMENT_OPTIONS },
                    { value: 'linkedinSummary', label: 'Linkedin Summary' },
                    { value: 'skills', label: 'Skills' },
                    { value: 'volunteering', label: 'Volunteering' },
                    { value: 'certifications', label: 'Certification' },
                    { value: 'education', label: 'Education' },
                    { value: 'projects', label: 'Projects' },
                    { value: 'recommendations', label: 'Recommendations' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PERSONAL_DATA })),

               ...[
                    { value: 'currentTitle', label: 'Current Title' },
                    { value: 'currentCompany', label: 'Current Company' },
                    { value: 'currentCompanyRevenue', label: 'Current Company Revenue' },
                    { value: 'currentCompanyPrimaryIndustry', label: 'Current Company Primary Industry' },
                    { value: 'currentCompanyDomain', label: 'Current Company Domain' },
                    { value: 'employees', label: 'Current Company Total Employees' },
                    { value: 'industries', label: 'Current Company Products & Services Tags' },
                    { value: 'relatedCompanies', label: 'Related Companies' },
                    { value: 'otherRoles', label: 'Other Roles' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.COMPANIES })),

               ...[
                    { value: 'userSourceFirst', label: 'Lifetime User Source - First' },
                    { value: 'userSourcePath', label: 'Lifetime User Source - Path' },
                    { value: 'noneDirectSourcePath', label: 'Lifetime Non-Direct User Source - Path' },
                    { value: 'userSourceLast', label: 'Lifetime User Source - Last' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.USER_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'channelFirst', label: 'Channel - First' },
                    { value: 'channelLast', label: 'Channel - Last' },
                    { value: 'channelPath', label: 'Channel - Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),

               ...[
                    { value: 'sessionIDFirst', label: 'Session ID (first)' },
                    { value: 'sessionIDLast', label: 'Session ID (last)' },
                    { value: 'lifetimeSessionIDPath', label: 'Lifetime Session ID (path)' },
                    { value: 'lifetimeSessionIDPathND', label: 'Lifetime Session ID <br>(path non-direct)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SESSION, sectionParent: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'identifiedSessionIDFirst', label: 'Created Session ID (first)' },
                    { value: 'identifiedSessionIDFirstND', label: 'Created Session ID <br>(first non-direct)' },
                    { value: 'identifiedSessionIDLast', label: 'Created Session ID (last)' },
                    { value: 'identifiedSessionIDLastND', label: 'Created Session ID <br>(last non-direct)' },
                    { value: 'identifiedSessionIDPath', label: 'Created Session ID (path)' },
                    { value: 'identifiedSessionIDPathND', label: 'Created Session ID <br>(path non-direct)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SESSION, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: 'userSourceFirstPersonCreated', label: 'User Source - First <br>(person created)' },
                    { value: 'userSourcePathPersonCreated', label: 'User Source - Path <br>(person created)' },
                    { value: 'userSourceLastPersonCreated', label: 'User Source - Last <br>(person created)' },
                    { value: 'identifiedSourceFirstND', label: 'Non-Direct User Source - First <br>(person created)' },
                    { value: 'identifiedSourcePathND', label: 'Non-Direct User Source - Path <br>(person created)' },
                    { value: 'identifiedSourceLastND', label: 'Non-Direct User Source - Last <br>(person created)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_USER_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: 'creationDate', label: 'Creation Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'lastConversionDate', label: 'Last Conversion Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'lastEventDate', label: 'Last Event Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'firstActiveAt', label: 'First Active', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'revealedDate', label: 'Revealed Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'providedDate', label: 'First Provided Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'importedDate', label: 'Imported Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'identifiedHour', label: 'Creation Hour', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'identifiedDayOfWeek', label: 'Creation Day of Week', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'identifiedMonth', label: 'Creation Month', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'identifiedYearMonth', label: 'Creation Year Month' },
                    { value: 'identifiedQuarter', label: 'Creation Quarter', type: SEGMENT_FIELD_TYPE.MULTI_VALUE },
                    { value: 'identifiedYearQuarter', label: 'Creation Year Quarter' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE })),

               ...[{ value: 'userId', label: 'User ID' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.IDENTIFIER_RELATIONSHIPS })),

               ...[
                    { value: 'identifiedSources.referrerHostname', label: 'Referrer Hostname' },
                    { value: 'identifiedSources.referrerOriginPath', label: 'Referrer Origin Path' },
                    { value: 'identifiedSources.referrerPath', label: 'Referrer Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: 'identifiedSources.utm_medium', label: 'utm_medium' },
                    { value: 'identifiedSources.utm_source', label: 'utm_source' },
                    { value: 'identifiedSources.utm_campaign', label: 'utm_campaign' },
                    { value: 'identifiedSources.utm_term', label: 'utm_term' },
                    { value: 'identifiedSources.utm_content', label: 'utm_content' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: 'identifiedSources.gclid', label: 'Click IDs (gclid)' },
                    { value: 'identifiedSources.fbclid', label: 'Click IDs (fbclid)' },
                    { value: 'identifiedSources._fbp', label: 'Click IDs (_fbp)' },
                    { value: 'identifiedSources._fbc', label: 'Click IDs (_fbc)' },
                    { value: 'identifiedSources.msclkid', label: 'Click IDs (msclkid)' },
                    { value: 'identifiedSources.li_fat_id', label: 'Click IDs (li_fat_id)' },
                    { value: 'identifiedSources.ttclid', label: 'Click IDs (ttclid)' },
                    { value: 'identifiedSources.dclid', label: 'Click IDs (dclid)' },
                    { value: 'identifiedSources.srsltid', label: 'Click IDs (srsltid)' },
                    { value: 'identifiedSources._ga', label: 'Click IDs (_ga)' },
                    { value: 'identifiedSources.calltrk_user', label: 'Click IDs (calltrk_user)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLICK_IDS, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: 'isTargetAccount', label: 'Is Target Account', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'targetAccountGroups', label: 'Target Account Groups' },
                    { value: 'targetAccountDomain', label: 'Target Account Domain' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT })),
          ],
          metric: [
               ...[
                    { value: 'eventCount', label: 'Events' },
                    { value: 'sessionCount', label: 'Sessions' },
                    { value: 'avgSessionDuration', label: 'Avg Session Duration' },
                    { value: 'engagedSessionCount', label: 'Engaged Sessions' },
                    { value: 'engagementRate', label: 'Engagement Rate' },
                    { value: 'engagementScore', label: 'Lifetime Engagement Score' },
                    { value: 'bouncedSessionCount', label: 'Bounced Sessions' },
                    { value: 'bounceRate', label: 'Bounce Rate' },
                    { value: 'pageViewCount', label: 'Pageviews' },
                    { value: 'avgPageViewsPerSession', label: 'Avg Pageviews / Session' },
                    { value: 'avgVisibleTimePerPage', label: 'Avg Visible Time / Page' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY })),

               ...[
                    { value: 'sourcePathCount', label: 'Source Path Count' },
                    { value: 'noneDirectSourcePathCount', label: 'Non-Direct Source Path Count' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...[
                    { value: 'conversionCount', label: 'All Conversions' },
                    { value: 'primaryConversionCount', label: 'Primary Conversions' },
                    { value: 'secondaryConversionCount', label: 'Secondary Conversions' },
                    { value: 'conversionRateAll', label: 'Conv Rate (All Conversions)' },
                    { value: 'conversionRatePrimary', label: 'Conv Rate <br>(Primary Conversions)' },
                    { value: 'conversionRateSecondary', label: 'Conv Rate <br>(Secondary Conversions)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),

               ...[{ value: 'revealedCompanyCount', label: 'Revealed Company Count' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.COMPANY_DETAILS })),

               ...[
                    { value: 'identifiedSourcePathCount', label: 'Created Source Path Count' },
                    { value: 'identifiedSourcePathCountND', label: 'Created Source Path Count <br>(non-direct)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),
          ],
     },
     [SEGMENT_OBJECT.COMPANY]: {
          property: [
               ...[
                    { value: 'companyName', label: 'Company Name' },
                    { value: 'domain', label: 'Domain' },
                    { value: 'dataSources', label: 'Data Sources' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.GENERAL_INFORMATION })),

               ...[
                    { value: 'revenue', label: 'Revenue' },
                    { value: 'employees', label: 'Total Employees' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SIZE })),

               ...[
                    { value: 'primaryIndustry', label: 'Primary Industry' },
                    { value: 'industries', label: 'Products & Services Tags' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.INDUSTRY })),

               ...[
                    { value: 'companyCountry', label: 'Country' },
                    { value: 'companyState', label: 'State' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LOCATION })),

               ...[
                    { value: 'createdDate', label: 'Creation date' },
                    { value: 'latestUpdateDate', label: 'Latest update date' },
                    { value: 'lastConversionDate', label: 'Last Conversion Date' },
                    { value: 'dataSourcesRevealedDate', label: 'Revealed Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'dataSourcesProvidedDate', label: 'First Provided Date', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'dataSourcesImportedDate', label: 'Imported Date', type: SEGMENT_FIELD_TYPE.DATE },
               ].map((item) => ({ ...item, type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.DATE })),

               ...[
                    { value: 'userSourceFirst', label: 'User Source - First' },
                    { value: 'userSourcePath', label: 'User Source - Path' },
                    { value: 'noneDirectSourcePath', label: 'Non-Direct User Source - Path' },
                    { value: 'userSourceLast', label: 'User Source - Last' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.USER_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE_SUMMARY })),

               ...[
                    { value: 'channelFirst', label: 'Channel - First' },
                    { value: 'channelLast', label: 'Channel - Last' },
                    { value: 'channelPath', label: 'Channel - Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),

               ...[
                    { value: 'isTargetAccount', label: 'Is Target Account', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'targetAccountGroups', label: 'Target Account Groups' },
                    // { value: 'targetAccountDomain', label: 'Target Account Domain' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT })),

               // { value: 'companyID', label: 'Company ID' },
               // { value: 'revealedDate', label: 'Revealed Date' },
               // { value: 'lastActiveDate', label: 'Last Active Date' },
               // { value: 'revealedHour', label: 'Revealed Hour' },
               // { value: 'revealedDayOfWeek', label: 'Revealed Day of Week' },
               // { value: 'revealedMonth', label: 'Revealed Month' },
               // { value: 'revealedYearMonth', label: 'Revealed Year Month' },
               // { value: 'revealedQuarter', label: 'Revealed Quarter' },
               // { value: 'revealedYearQuarter', label: 'Revealed Year Quarter' },
               // { value: 'descriptionShort', label: 'Short Description' },
               // { value: 'description', label: 'Long Description' },
               // { value: 'yearFounded', label: 'Year Founded' },

               // { value: 'country', label: 'Country' },
               // { value: 'countryCode', label: 'Country Code' },
               // { value: 'region', label: 'Region' },
               // { value: 'regionCode', label: 'Region Code' },
               // { value: 'city', label: 'City' },
               // { value: 'postalCode', label: 'Postal Code' },
               // { value: 'timezoneName', label: 'Timezone Name' },
               // { value: 'alexaRange', label: 'Alexa Rank' },
               // { value: 'monthlyVisitors', label: 'Monthly Visitors' },
               // { value: 'businessType', label: 'Business Type' },
               // { value: 'stockExchange', label: 'Stock Exchange' },
               // { value: 'tickerSymbol', label: 'Ticker Symbol' },
               // { value: 'naicsCode', label: 'NAICS Code' },
               // { value: 'naicsSector', label: 'NAICS Sector' },
               // { value: 'naicsSubsector', label: 'NAICS Subsector' },
               // { value: 'naicsIndustryGroup', label: 'NAICS Industry Group' },
               // { value: 'naicsIndustry', label: 'NAICS Industry' },
               // { value: 'naicsDescription', label: 'NAICS Description' },
               // { value: 'sicCode', label: 'SIC Code' },
               // { value: 'sicIndustryGroup', label: 'SIC Industry Group' },
               // { value: 'sicIndustry', label: 'SIC Industry' },
               // { value: 'isEngaged', label: 'Engaged' },
               // { value: 'revealedSessionID', label: 'Revealed Session ID' },
               // { value: 'createdSourceFirst', label: 'Created Source (first)' },
               // { value: 'createdSourceFirstND', label: 'Created Source (first non-direct)' },
               // { value: 'createdSourceLast', label: 'Created Source (last)' },
               // { value: 'createdSourceLastND', label: 'Created Source (last non-direct)' },
               // { value: 'createdSourcePath', label: 'Created Source (path)' },
               // { value: 'createdSourcePathND', label: 'Created Source (path non-direct)' },

               // { value: 'createdSources.sessionID', label: 'Created Sources (Source Session ID)' },
               // { value: 'createdSources.source', label: 'Created Sources (Source)' },
               // { value: 'createdSources.timestamp', label: 'Created Sources (Timestamp)' },
               // { value: 'createdSources.referrerHostname', label: 'Created Sources (Referrer Hostname)' },
               // { value: 'createdSources.referrerOriginPath', label: 'Created Sources (Referrer Origin Path)' },
               // { value: 'createdSources.referrerPath', label: 'Created Sources (Referrer Path)' },
               // { value: 'createdSources.utm_medium', label: 'Created Sources (utm_medium)' },
               // { value: 'createdSources.utm_source', label: 'Created Sources (utm_source)' },
               // { value: 'createdSources.utm_campaign', label: 'Created Sources (utm_campaign)' },
               // { value: 'createdSources.utm_term', label: 'Created Sources (utm_term)' },
               // { value: 'createdSources.utm_content', label: 'Created Sources (utm_content)' },
               // { value: 'createdSources.gclid', label: 'Created Sources Click IDs (gclid)' },
               // { value: 'createdSources.fbclid', label: 'Created Sources Click IDs (fbclid)' },
               // { value: 'createdSources._fbp', label: 'Created Sources Click IDs (_fbp)' },
               // { value: 'createdSources._fbc', label: 'Created Sources Click IDs (_fbc)' },
               // { value: 'createdSources.msclkid', label: 'Created Sources Click IDs (msclkid)' },
               // { value: 'createdSources.li_fat_id', label: 'Created Sources Click IDs (li_fat_id)' },
               // { value: 'createdSources.ttclid', label: 'Created Sources Click IDs (ttclid)' },
               // { value: 'createdSources.dclid', label: 'Created Sources Click IDs (dclid)' },
               // { value: 'createdSources.srsltid', label: 'Created Sources Click IDs (srsltid)' },
               // { value: 'createdSources._ga', label: 'Created Sources Click IDs (_ga)' },
               // { value: 'createdSources.calltrk_user', label: 'Created Sources Click IDs (calltrk_user)' },
               // { value: 'createdSources.weights.engagement', label: 'Created Sources Weights (Engagement)' },
               // { value: 'createdSources.weights.engagementND', label: 'Created Sources Weights (Engagement Non Direct)' },
               // { value: 'createdSources.weights.firstTouch', label: 'Created Sources Weights (First Touch)' },
               // { value: 'createdSources.weights.firstTouchND', label: 'Created Sources Weights (First Touch Non Direct)' },
               // { value: 'createdSources.weights.lastTouch', label: 'Created Sources Weights (Last Touch)' },
               // { value: 'createdSources.weights.lastTouchND', label: 'Created Sources Weights (Last Touch Non Direct)' },
               // { value: 'createdSources.weights.uShaped', label: 'Created Sources Weights (U-Shaped)' },
               // { value: 'createdSources.weights.uShapedND', label: 'Created Sources Weights (U-Shaped Non Direct)' },
               // { value: 'createdSources.weights.linear', label: 'Created Sources Weights (Linear)' },
               // { value: 'createdSources.weights.linearND', label: 'Created Sources Weights (Linear Non Direct)' },

               // { value: 'sourceFirstND', label: 'Source (first non-direct)' },
               // { value: 'sourceLastND', label: 'Source (last non-direct)' },
               // { value: 'sources.sessionID', label: 'Sources (Source Session ID)' },
               // { value: 'sources.timestamp', label: 'Sources (Timestamp)' },
               // { value: 'sources.source', label: 'Source' },
               // { value: 'sources.referrerHostname', label: 'Referrer Hostname' },
               // { value: 'sources.referrerOriginPath', label: 'Referrer Origin Path' },
               // { value: 'sources.referrerPath', label: 'Referrer Path' },
               // { value: 'sources.utm_medium', label: 'utm_medium' },
               // { value: 'sources.utm_source', label: 'utm_source' },
               // { value: 'sources.utm_campaign', label: 'utm_campaign' },
               // { value: 'sources.utm_term', label: 'utm_term' },
               // { value: 'sources.utm_content', label: 'utm_content' },
               // { value: 'sources.gclid', label: 'Click IDs (gclid)' },
               // { value: 'sources.fbclid', label: 'Click IDs (fbclid)' },
               // { value: 'sources._fbp', label: 'Click IDs (_fbp)' },
               // { value: 'sources._fbc', label: 'Click IDs (_fbc)' },
               // { value: 'sources.msclkid', label: 'Click IDs (msclkid)' },
               // { value: 'sources.li_fat_id', label: 'Click IDs (li_fat_id)' },
               // { value: 'sources.ttclid', label: 'Click IDs (ttclid)' },
               // { value: 'sources.dclid', label: 'Click IDs (dclid)' },
               // { value: 'sources.srsltid', label: 'Click IDs (srsltid)' },
               // { value: 'sources._ga', label: 'Click IDs (_ga)' },
               // { value: 'sources.calltrk_user', label: 'Click IDs (calltrk_user)' },
          ],
          metric: [
               ...[
                    { value: 'peopleCount', label: 'People' },
                    { value: 'userCount', label: 'Users' },
                    { value: 'sessionCount', label: 'Sessions' },
                    { value: 'eventCount', label: 'Events' },
                    { value: 'engagedSessionCount', label: 'Engaged Sessions' },
                    { value: 'engagementScore', label: 'Lifetime Engagement Score' },
                    { value: 'avgSessionDuration', label: 'Avg Session Duration' },
                    { value: 'avgVisibleTimePerPage', label: 'Avg Visible Time / Page' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY })),

               ...[
                    { value: 'sourcePathCount', label: 'Source Path Count' },
                    { value: 'noneDirectSourcePathCount', label: 'Non-Direct Source Path Count' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.REVEALED_SOURCE_SUMMARY })),

               ...[
                    { value: 'conversionCount', label: 'All Conversions' },
                    { value: 'primaryConversionCount', label: 'Primary Conversions' },
                    { value: 'secondaryConversionCount', label: 'Secondary Conversions' },
                    { value: 'conversionRateAll', label: 'Conv Rate (All Conversions)' },
                    { value: 'conversionRatePrimary', label: 'Conv Rate <br>(Primary Conversions)' },
                    { value: 'conversionRateSecondary', label: 'Conv Rate <br>(Secondary Conversions)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),

               // { value: 'employeeCountEstimate', label: 'Employee Exact Estimate' },
               // { value: 'engagementScore.10Days', label: 'Previous 10 Days\' Engagement Score' },
               // { value: 'engagementScore.30Days', label: 'Previous 30 Days\' Engagement Score' },
               // { value: 'revealedCompanyCount', label: 'Revealed Company Count' },
               // { value: 'sessionDuration', label: 'Session Duration' },
               // { value: 'pageViewCount', label: 'Pageview Count' },
               // { value: 'conversionValue', label: 'Conversion Value' },
               // { value: 'primaryConvValue', label: 'Primary Conversion Value (Lifetime)' },
               // { value: 'secondaryConvValue', label: 'Secondary Conversion Value (Lifetime)' },
          ],
     },
     [SEGMENT_OBJECT.CRM_LEAD]: {
          property: [
               // Date (Account)
               ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,

               // Record Info
               ...[
                    { value: 'crmCompany', label: 'Company' },
                    { value: 'crmFirstName', label: 'First Name' },
                    { value: 'crmLastName', label: 'Last Name' },
                    { value: 'crmExternalID', label: 'ExternalID' },
                    { value: 'crmEmail', label: 'Email' },
                    { value: 'crmSecondaryEmail', label: 'Secondary Email' },
                    { value: 'crmPersonalPhone', label: ' Personal Phone' },
                    { value: 'crmSecondaryPhone', label: 'Secondary Phone' },
                    { value: 'crmCity', label: 'City' },
                    { value: 'crmCountry', label: 'Country' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmStatus', label: 'Status' },
                    { value: 'crmIsConverted', label: 'IsConverted', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsQualified', label: 'IsQualified', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmRating', label: 'Rating' },
                    { value: 'crmDisposition', label: 'Disposition', type: BIGQUERY_FIELD_TYPE.NUMERIC },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Targeting Fields
               ...[
                    { value: 'crmEmailOptOut', label: 'Email Opt Out', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmAdvertisingOptOut', label: 'Advertising Opt Out', type: SEGMENT_FIELD_TYPE.BOOLEAN },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGETING_FIELDS })),

               // Created Sources
               ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

               // Created Channels
               ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

               // Created UTMs
               ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

               ...[
                    { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
                    { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               ...[
                    { value: 'sourcePath', label: 'Lifetime Source (path)' },
                    { value: 'sourcePathND', label: 'Lifetime Source (path) Nondirect' },
                    { value: 'sources.landingPageOriginPath', label: 'Lifetime Landing Page (path)' },
                    { value: 'sources.referrerOriginPath', label: 'Lifetime Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Created Click IDs
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,

               // Imported Click IDs
               ...CRM_IMPORTED_CLICK_ID_PROPERTY_OPTIONS,
               // Custom Properties > [any custom dimension registered on this object]
          ],
          metric: [
               // Pipeline & Status
               ...[
                    { value: 'crmEngagedScore', label: 'Sales Engaged Score' },
                    { value: 'crmFitScore', label: 'Sales Fit Score' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // ListenLayer Objects
               ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

               // External Objects
               ...[
                    { value: 'crmContactCount', label: 'Contacts (lifetime)' },
                    { value: 'crmAccountCount', label: 'Accounts (lifetime)' },
                    { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
                    { value: 'crmCampaignCount', label: 'Campaign (lifetime)' },
                    { value: 'crmEventCount', label: 'Events (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Engagement
               ...CRM_ENGAGEMENT_METRIC_OPTIONS,

               // Lifetime Web Conversions
               ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
     [SEGMENT_OBJECT.CRM_CONTACT]: {
          property: [
               // Date (Account)
               ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,

               // Record Info
               ...[
                    { value: 'crmFirstName', label: 'First Name' },
                    { value: 'crmLastName', label: 'Last Name' },
                    { value: 'crmExternalID', label: 'ExternalID' },
                    { value: 'crmEmail', label: 'Email' },
                    { value: 'crmSecondaryEmail', label: 'Secondary Email' },
                    { value: 'crmPersonalPhone', label: ' Personal Phone' },
                    { value: 'crmSecondaryPhone', label: 'Secondary Phone' },
                    { value: 'crmCity', label: 'City' },
                    { value: 'crmCountry', label: 'Country' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmIsQualified', label: 'IsQualified', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmStatus', label: 'Status' },
                    { value: 'crmIsCustomer', label: 'IsCustomer', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsCustomerDate', label: 'isCustomerDate', type: SEGMENT_FIELD_TYPE.DATE},
                    { value: 'crmIsLostCustomer', label: 'isLostCustomer', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsLostCustomerDate', label: 'isLostCustomerDate', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'crmDisposition', label: 'Disposition', type: BIGQUERY_FIELD_TYPE.NUMERIC },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Targeting Fields
               ...[
                    { value: 'crmEmailOptOut', label: 'Email Opt Out', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmAdvertisingOptOut', label: 'Advertising Opt Out', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmDoNotCall', label: 'Do Not Call', type: SEGMENT_FIELD_TYPE.BOOLEAN },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGETING_FIELDS })),

               // Created Sources
               ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

               // Created Channels
               ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

               // Created UTMs
               ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

               ...[
                    { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
                    { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               ...[
                    { value: 'sourcePath', label: 'Lifetime Source (path)' },
                    { value: 'sourcePathND', label: 'Lifetime Source (path) Nondirect' },
                    { value: 'sources.landingPageOriginPath', label: 'Lifetime Landing Page (path)' },
                    { value: 'sources.referrerOriginPath', label: 'Lifetime Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Created Click IDs
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,

               // Imported Click IDs
               ...CRM_IMPORTED_CLICK_ID_PROPERTY_OPTIONS,
               // Custom Properties > [any custom dimension registered on this object]
          ],
          metric: [
               // Pipeline & Status
               ...[
                    { value: 'crmEngagedScore', label: 'Sales Engaged Score' },
                    { value: 'crmFitScore', label: 'Sales Fit Score' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // ListenLayer Objects
               ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

               // External Objects
               ...[
                    { value: 'crmLeadCount', label: 'Leads (lifetime)' },
                    { value: 'crmAccountCount', label: 'Accounts (lifetime)' },
                    { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
                    { value: 'crmCampaignCount', label: 'Campaign (lifetime)' },
                    { value: 'crmQuoteCount', label: 'Quotes (lifetime)' },
                    { value: 'crmOrderCount', label: 'Orders (lifetime)' },
                    { value: 'crmContractCount', label: 'Contracts (lifetime)' },
                    { value: 'crmEventCount', label: 'Events (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Engagement
               ...CRM_ENGAGEMENT_METRIC_OPTIONS,

               // Lifetime Web Conversions
               ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
     [SEGMENT_OBJECT.CRM_ACCOUNT]: {
          property: [
               // Date (Account)
               ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,

               // Record Info
               ...[
                    { value: 'crmAccountName', label: 'Account (Business) Name' },
                    { value: 'crmAccountType', label: 'Account Type' },
                    { value: 'crmAccountIndustry', label: 'Industry' },
                    { value: 'crmAccountOwnershipType', label: 'Ownership Type' },
                    { value: 'crmAccountPhone', label: 'Phone' },
                    { value: 'crmDomain', label: 'Domain' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmStatus', label: 'Status' },
                    { value: 'crmIsPartner', label: 'IsPartner', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsQualified', label: 'IsQualified', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmRating', label: 'Rating' },
                    { value: 'crmIsCustomer', label: 'IsCustomer', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsCustomerDate', label: 'isCustomerDate', type: SEGMENT_FIELD_TYPE.DATE},
                    { value: 'crmIsLostCustomer', label: 'isLostCustomer', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsLostCustomerDate', label: 'isLostCustomerDate', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'crmDisposition', label: 'Disposition', type: BIGQUERY_FIELD_TYPE.NUMERIC },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Created Sources
               ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

               // Created Channels
               ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

               // Created UTMs
               ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

               ...[
                    { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
                    { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               ...[
                    { value: 'sourcePath', label: 'Lifetime Source (path)' },
                    { value: 'sourcePathND', label: 'Lifetime Source (path) Nondirect' },
                    { value: 'sources.landingPageOriginPath', label: 'Lifetime Landing Page (path)' },
                    { value: 'sources.referrerOriginPath', label: 'Lifetime Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Created Click IDs
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metric: [
               // Record Info
               ...[{ value: 'crmAccountAnnualRevenue', label: 'Annual Revenue' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmEngagedScore', label: 'Sales Engaged Score' },
                    { value: 'crmFitScore', label: 'Sales Fit Score' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // ListenLayer Objects
               ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

               // External Objects
               ...[
                    { value: 'crmLeadCount', label: 'Leads (lifetime)' },
                    { value: 'crmContactCount', label: 'Contacts (lifetime)' },
                    { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
                    { value: 'crmCampaignCount', label: 'Campaigns (lifetime)' },
                    { value: 'crmQuoteCount', label: 'Quotes (lifetime)' },
                    { value: 'crmOrderCount', label: 'Orders (lifetime)' },
                    { value: 'crmContractCount', label: 'Contracts (lifetime)' },
                    { value: 'crmEventCount', label: 'Events (lifetime)' },
                    { value: 'crmParentAccountCount', label: 'Parent Accounts (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Engagement
               ...CRM_ENGAGEMENT_METRIC_OPTIONS,

               // Lifetime Web Conversions
               ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
     [SEGMENT_OBJECT.CRM_OPPORTUNITY]: {
          property: [
               // Date (Account)
               ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,
               ...[
                    { value: 'closedDate', label: 'Closed Date' },
                    { value: 'forecastedClosedDate', label: 'Forecast Closed Date' },
               ].map((item) => ({ ...item, type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),

               // Record Info
               ...[
                    { value: 'crmOpportunityName', label: 'Opportunity Name' },
                    { value: 'crmOpportunityType', label: 'Opportunity Type' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmIsQualified', label: 'IsQualified', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmStatus', label: 'Status' },
                    { value: 'crmStage', label: 'Stage' },
                    { value: 'crmIsWon', label: 'IsWon', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsClosed', label: 'IsClosed', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsLost', label: 'IsLost', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmDisposition', label: 'Disposition', type: BIGQUERY_FIELD_TYPE.NUMERIC },
                    { value: 'primarySalesStatus', label: 'Primary Sales Status' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Values
               ...CRM_VALUE_PROPERTY_OPTIONS,

               // Created Sources
               ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

               // Created Channels
               ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

               // Created UTMs
               ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

               ...[
                    { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
                    { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Closed Sources
               ...CRM_CLOSED_SOURCE_PROPERTY_OPTIONS,

               ...[
                    { value: 'closedSources.landingPageOriginPath', label: 'Closed Landing Page (path)' },
                    { value: 'closedSources.referrerOriginPath', label: 'Closed Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Created Click IDs
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
               // Custom Properties > [any custom dimension registered on this object]
          ],
          metric: [
               // Pipeline & Status
               ...[
                    // { value: '', label: 'Sales Probability' },
                    { value: 'crmEngagedScore', label: 'Sales Engaged Score' },
                    { value: 'crmFitScore', label: 'Sales Fit Score' },
                    { value: 'crmAgeInDays', label: 'Age In Days' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Values
               ...CRM_VALUE_METRIC_OPTIONS,

               // ListenLayer Objects
               ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

               // External Objects
               ...[
                    { value: 'crmLeadCount', label: 'Leads (lifetime)' },
                    { value: 'crmAccountCount', label: 'Accounts (lifetime)' },
                    { value: 'crmContactCount', label: 'Contacts (lifetime)' },
                    { value: 'crmCampaignCount', label: 'Campaigns (lifetime)' },
                    { value: 'crmQuoteCount', label: 'Quotes (lifetime)' },
                    { value: 'crmOrderCount', label: 'Orders (lifetime)' },
                    { value: 'crmContractCount', label: 'Contracts (lifetime)' },
                    { value: 'crmEventCount', label: 'Events (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Engagement
               ...CRM_ENGAGEMENT_METRIC_OPTIONS,

               // Lifetime Web Conversions
               ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
     [SEGMENT_OBJECT.CRM_QUOTE]: {
          property: [
               // Date (Account)
               ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,
               ...[
                    { value: 'closedDate', label: 'Closed Date' },
                    { value: 'forecastedAcceptanceDate', label: 'Forecasted Acceptance Date' },
               ].map((item) => ({ ...item, type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),

               // Record Info
               ...[{ value: 'crmQuoteName', label: 'Quote Name' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmStatus', label: 'Status' },
                    { value: 'crmIsAccepted', label: 'IsAccepted', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'primarySalesStatus', label: 'Primary Sales Status' },
                    { value: 'crmIsClosed', label: 'IsClosed', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmDisposition', label: 'Disposition', type: BIGQUERY_FIELD_TYPE.NUMERIC },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Values
               ...CRM_VALUE_PROPERTY_OPTIONS,

               // Created Sources
               ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

               // Created Channels
               ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

               // Created UTMs
               ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

               ...[
                    { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
                    { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Closed Sources
               ...CRM_CLOSED_SOURCE_PROPERTY_OPTIONS,

               ...[
                    { value: 'closedSources.landingPageOriginPath', label: 'Closed Landing Page (path)' },
                    { value: 'closedSources.referrerOriginPath', label: 'Closed Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Created Click IDs
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
               // Custom Properties > [any custom dimension registered on this object]
          ],
          metric: [
               // Values
               ...CRM_VALUE_METRIC_OPTIONS,

               // ListenLayer Objects
               ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

               // External Objects
               ...[
                    { value: 'crmAccountCount', label: 'Accounts (lifetime)' },
                    { value: 'crmContactCount', label: 'Contacts (lifetime)' },
                    { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
                    { value: 'crmOrderCount', label: 'Orders (lifetime)' },
                    { value: 'crmContractCount', label: 'Contracts (lifetime)' },
                    { value: 'crmEventCount', label: 'Events (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Engagement
               ...CRM_ENGAGEMENT_METRIC_OPTIONS,

               // Lifetime Web Conversions
               ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
     [SEGMENT_OBJECT.CRM_ORDER]: {
          property: [
               // Date (Account)
               ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,
               ...[
                    { value: 'closedDate', label: 'Closed Date' },
                    { value: 'forecastedActivationDate', label: 'Forecasted Activation Date' },
               ].map((item) => ({ ...item, type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),

               // Record Info
               ...[
                    { value: 'crmOrderName', label: 'Order Name' },
                    { value: 'crmOrderNumber', label: 'Order Number' },
                    { value: 'crmOrderType', label: 'Order Type' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmStatus', label: 'Status' },
                    { value: 'primarySalesStatus', label: 'Primary Sales Status' },
                    { value: 'crmIsAccepted', label: 'IsAccepted', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmIsClosed', label: 'IsClosed', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmDisposition', label: 'Disposition', type: BIGQUERY_FIELD_TYPE.NUMERIC },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Values
               ...CRM_VALUE_PROPERTY_OPTIONS,

               // Created Sources
               ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

               // Created Channels
               ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

               // Created UTMs
               ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

               ...[
                    { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
                    { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Closed Sources
               ...CRM_CLOSED_SOURCE_PROPERTY_OPTIONS,

               ...[
                    { value: 'closedSources.landingPageOriginPath', label: 'Closed Landing Page (path)' },
                    { value: 'closedSources.referrerOriginPath', label: 'Closed Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Created Click IDs
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
               // { value: '', label: '[any custom dimension registered on this object]' },
          ],
          metric: [
               // Values
               ...CRM_VALUE_METRIC_OPTIONS,

               // ListenLayer Objects
               ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

               // External Objects
               ...[
                    { value: 'crmAccountCount', label: 'Accounts (lifetime)' },
                    { value: 'crmContactCount', label: 'Contacts (lifetime)' },
                    { value: 'crmQuoteCount', label: 'Quotes (lifetime)' },
                    { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
                    { value: 'crmContractCount', label: 'Contracts (lifetime)' },
                    { value: 'crmEventCount', label: 'Events (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Engagement
               ...CRM_ENGAGEMENT_METRIC_OPTIONS,

               // Lifetime Web Conversions
               ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
     [SEGMENT_OBJECT.CRM_CONTRACT]: {
          property: [
               // Date (Account)
               ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,
               ...[
                    { value: 'closedDate', label: 'Closed Date' },
                    { value: 'forecastedActivationDate', label: 'Forecasted Activation Date' },
               ].map((item) => ({ ...item, type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),

               // Record Info
               ...[
                    { value: 'crmContractName', label: 'Contract Name' },
                    { value: 'crmContractNumber', label: 'Contract Number' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmStatus', label: 'Status' },
                    { value: 'crmIsClosed', label: 'IsClosed', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'primarySalesStatus', label: 'Primary Sales Status' },
                    { value: 'crmIsAccepted', label: 'IsAccepted', type: SEGMENT_FIELD_TYPE.BOOLEAN },
                    { value: 'crmDisposition', label: 'Disposition', type: BIGQUERY_FIELD_TYPE.NUMERIC },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Values
               ...CRM_VALUE_PROPERTY_OPTIONS,

               // Created Sources
               ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

               // Created Channels
               ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

               // Created UTMs
               ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

               ...[
                    { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
                    { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Closed Sources
               ...CRM_CLOSED_SOURCE_PROPERTY_OPTIONS,

               ...[
                    { value: 'closedSources.landingPageOriginPath', label: 'Closed Landing Page (path)' },
                    { value: 'closedSources.referrerOriginPath', label: 'Closed Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Created Click IDs
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
               // Custom Properties > [any custom dimension registered on this object]
          ],
          metric: [
               // Values
               ...CRM_VALUE_METRIC_OPTIONS,

               // ListenLayer Objects
               ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

               // External Objects
               ...[
                    { value: 'crmAccountCount', label: 'Accounts (lifetime)' },
                    { value: 'crmOrderCount', label: 'Orders (lifetime)' },
                    { value: 'crmQuoteCount', label: 'Quotes (lifetime)' },
                    { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
                    { value: 'crmContactCount', label: 'Contacts (lifetime)' },
                    { value: 'crmEventCount', label: 'Events (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Engagement
               ...CRM_ENGAGEMENT_METRIC_OPTIONS,

               // Lifetime Web Conversions
               ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
     [SEGMENT_OBJECT.CRM_CAMPAIGN]: {
          property: [
               // Date (Account)
               ...[
                    { value: 'createdDate', label: 'Created Date' },
                    { value: 'lastModifiedAt', label: 'Last Modified' },
               ].map((item) => ({ ...item, type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),

               // Record Info
               ...[{ value: 'crmCampaignName', label: 'Campaign Name' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Pipeline & Status
               ...[
                    { value: 'crmStatus', label: 'Status' },
                    { value: 'crmDisposition', label: 'Disposition', type: BIGQUERY_FIELD_TYPE.NUMERIC },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // Custom Properties > [any custom dimension registered on this object]
          ],
          metric: [
               // External Objects
               ...[
                    { value: 'crmContactCount', label: 'Contacts (lifetime)' },
                    { value: 'crmLeadCount', label: 'Leads (lifetime)' },
                    { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
                    { value: 'crmEventCount', label: 'Events (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
     [SEGMENT_OBJECT.CRM_EVENT]: {
          property: [
               // Date (Account)
               ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,

               // Record Info
               ...[
                    { value: 'crmActivityEventSubject', label: 'Activity Event Subject' },
                    { value: 'crmActivityEventStartDateTime', label: 'Activity Event StartDateTime', type: SEGMENT_FIELD_TYPE.DATE },
                    { value: 'crmActivityEventLocation', label: 'Activity Event Location' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

               // Created Sources
               ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

               // Created Channels
               ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

               // Created UTMs
               ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

               ...[
                    { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
                    { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               ...[
                    { value: 'sourcePath', label: 'Lifetime Source (path)' },
                    { value: 'sourcePathND', label: 'Lifetime Source (path) Nondirect' },
                    { value: 'sources.landingPageOriginPath', label: 'Lifetime Landing Page (path)' },
                    { value: 'sources.referrerOriginPath', label: 'Lifetime Referrer OriginPath (path)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

               // Created Click IDs
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,

               // Custom Properties > [any custom dimension registered on this object]
          ],
          metric: [
               // Pipeline & Status
               ...[{ value: 'crmDisposition', label: 'Disposition' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

               // ListenLayer Objects
               ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

               // External Objects
               ...[
                    { value: 'crmAccountCount', label: 'Accounts (lifetime)' },
                    { value: 'crmOrderCount', label: 'Orders (lifetime)' },
                    { value: 'crmQuoteCount', label: 'Quotes (lifetime)' },
                    { value: 'crmCampaignCount', label: 'Campaign (lifetime)' },
                    { value: 'crmContractCount', label: 'Contracts (lifetime)' },
                    { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
                    { value: 'crmContactCount', label: 'Contacts (lifetime)' },
                    { value: 'crmLeadCount', label: 'Leads (lifetime)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

               // Engagement
               ...CRM_ENGAGEMENT_METRIC_OPTIONS,

               // Lifetime Web Conversions
               ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

               // Lifetime Sales Conversions
               ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
          ],
     },
};

// Custom Object Property & Metric
export const CUSTOM_OBJECT_PROPERTY_METRIC_SEGMENT_OPTIONS = {
     property: [
          // Date (Account)
          ...CRM_DATE_ACCOUNT_PROPERTY_OPTIONS,
          ...[
               { value: 'closedDate', label: 'Closed Date' },
               { value: 'forecastedClosedDate', label: 'Forecast Closed Date' },
          ].map((item) => ({ ...item, type: SEGMENT_FIELD_TYPE.DATE, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),

          // Pipeline & Status
          ...[
               { value: 'crmIsClosed', label: 'IsClosed', type: SEGMENT_FIELD_TYPE.BOOLEAN },
               { value: 'primarySalesStatus', label: 'Primary Sales Status' },
               { value: 'crmIsAccepted', label: 'IsAccepted', type: SEGMENT_FIELD_TYPE.BOOLEAN },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),

          // Values
          ...CRM_VALUE_PROPERTY_OPTIONS,

          // Created Sources
          ...CRM_CREATED_SOURCE_PROPERTY_OPTIONS,

          // Created Channels
          ...CRM_CREATED_CHANNEL_PROPERTY_OPTIONS,

          // Created UTMs
          ...CRM_CREATED_UTM_PROPERTY_OPTIONS,

          ...[
               { value: 'createdSources.landingPageOriginPath', label: 'Created Landing Page (path)' },
               { value: 'createdSources.referrerOriginPath', label: 'Created Referrer OriginPath (path)' },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_BY, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

          ...[
               { value: 'sourcePath', label: 'Lifetime Source (path)' },
               { value: 'sourcePathND', label: 'Lifetime Source (path) Nondirect' },
               { value: 'sources.landingPageOriginPath', label: 'Lifetime Landing Page (path)' },
               { value: 'sources.referrerOriginPath', label: 'Lifetime Referrer OriginPath (path)' },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME, sectionParent: SECTION_OPTION_SEGMENT.SOURCES_JOURNEY })),

          // Lifetime Click IDs
          ...CRM_LIFETIME_CLICK_ID_PATH_PROPERTY_OPTIONS,

          // Imported Click IDs
          ...CRM_IMPORTED_CLICK_ID_PROPERTY_OPTIONS,

          // Custom Properties > [any custom dimension registered on this object]
     ],
     metric: [
          // Values
          ...CRM_VALUE_METRIC_OPTIONS,

          // ListenLayer Objects
          ...CRM_LISTENLAYER_OBJECT_METRIC_OPTIONS,

          // External Objects
          ...[
               { value: 'crmAccountCount', label: 'Accounts (lifetime)' },
               { value: 'crmOrderCount', label: 'Orders (lifetime)' },
               { value: 'crmQuoteCount', label: 'Quotes (lifetime)' },
               { value: 'crmCampaignCount', label: 'Campaign (lifetime)' },
               { value: 'crmContractCount', label: 'Contracts (lifetime)' },
               { value: 'crmOpportunityCount', label: 'Opportunities (lifetime)' },
               { value: 'crmContactCount', label: 'Contacts (lifetime)' },
               { value: 'crmLeadCount', label: 'Leads (lifetime)' },
               { value: 'crmEventCount', label: 'Events (lifetime)' },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),

          // Engagement
          ...CRM_ENGAGEMENT_METRIC_OPTIONS,

          // Lifetime Web Conversions
          ...CRM_LIFETIME_WEB_CONVERSION_METRIC_OPTIONS,

          // Lifetime Sales Conversions
          ...CRM_LIFETIME_SALES_CONVERSION_METRIC_OPTIONS,
     ],
};

const DATE_PROPERTY_OPTIONS = [
     { value: 'date', label: 'Date' },
     { value: 'hour', label: 'Hour' },
     { value: 'dayOfWeek', label: 'Day of Week' },
     { value: 'month', label: 'Month' },
     { value: 'yearMonth', label: 'Year Month' },
     { value: 'quarter', label: 'Quarter' },
     { value: 'yearQuarter', label: 'Year Quarter' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE }));

const CREATED_SOURCE_CLICKID_PROPERTY_OPTIONS = ['gclid', 'fbclid', 'fbp', 'fbc', 'msclkid', 'li_fat_id', 'ttclid', 'dclid', 'srsltid', '_ga', 'calltrk_user'].map(
     (name) => ({ value: `createdSources.clickIDs.${name}`, label: `Click IDs (${name})`, section: SECTION_OPTION_SEGMENT.CREATED_CLICK_IDS })
);

const CREATED_SOURCE_UTM_PROPERTY_OPTIONS = [
     { value: 'utm_medium', label: 'utm_medium' },
     { value: 'utm_source', label: 'utm_source' },
     { value: 'utm_source_medium', label: 'utm_source / utm_medium' },
     { value: 'utm_campaign', label: 'utm_campaign' },
     { value: 'utm_term', label: 'utm_term' },
     { value: 'utm_content', label: 'utm_content' },
].map((item) => ({ ...item, value: `createdSources.${item.value}`, section: SECTION_OPTION_SEGMENT.CREATED_UTM }));

const CRM_CREATED_CHANNEL_EVENT_PROPERTY_OPTIONS = [
     { value: 'createdChannelFirst', label: 'Created Channel (first)' },
     { value: 'createdChannelLast', label: 'Created Channel (last)' },
     { value: 'createdChannelPath', label: 'Created Channel (path)' },
].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_CHANNEL, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO }));

// Web Conversion Event
const WEB_CONVERSION_PROPERTY_SEGMENT_OPTIONS = [
     ...[
          { value: 'sessionID', label: 'Session ID' },
          { value: 'userID', label: 'User ID' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.IDENTIFIER_RELATIONSHIPS })),

     ...DATE_PROPERTY_OPTIONS,

     ...[
          { value: 'conversionType', label: 'Conversion Type' },
          { value: 'conversionValue', label: 'Conversion Value' },
          { value: 'conversionCurrency', label: 'Conversion Currency' },
          { value: 'conversionLocationHostname', label: 'Conversion Location (Hostname)' },
          { value: 'conversionLocationOriginPath', label: 'Conversion Location <br>(Origin Path)' },
          { value: 'conversionLocationURL', label: 'Conversion Location (URL)' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CONVERSIONS })),

     ...[
          { value: 'createdSessionIDFirst', label: 'session ID - first' },
          { value: 'createdSessionIDLast', label: 'session ID - last' },
          { value: 'createdSessionIDPath', label: 'session ID - path' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SESSION, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...[
          { value: 'createdSourceFirst', label: 'userSource - first' },
          { value: 'createdSourceLast', label: 'userSource - last' },
          { value: 'createdSourcePath', label: 'userSource - path' },
          { value: 'createdSessionIDFirstND', label: 'non-direct userSource first' },
          { value: 'createdSourceLastND', label: 'non-direct userSource last' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...[
          { value: 'createdSources.referrerHostname', label: 'Referrer Hostname' },
          { value: 'createdSources.referrerOriginPath', label: 'Referrer Origin Path' },
          { value: 'createdSources.referrerPath', label: 'Referrer Path' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE_REFERRER, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     // { value: 'createdSourceFirst', label: 'Created Source (first)', section: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_SUMMARY },
     // { value: 'createdSourceFirstND', label: 'Created Source (first non-direct)' },
     // { value: 'createdSessionIDLastND', label: 'Created Session ID (last non-direct)' },
     // { value: 'createdSourcePathCount', label: 'Created Source Path Count', section: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_SUMMARY },
     // { value: 'createdSessionIDPathND', label: 'Created Session ID (path non-direct)' },
     // { value: 'createdSourcePathND', label: 'Created Source (path non-direct)' },
     // { value: 'createdSourcePathCountND', label: 'Created Source Path Count (non-direct)' },
     // { value: 'createdSources.sessionID', label: 'Created Sources <br>(Source Session ID)', section: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_SUMMARY },
     // { value: 'createdSources.timestamp', label: 'Created Sources <br>(Source Timestamp)', section: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_SUMMARY },
     // { value: 'createdSources.source', label: 'Created Sources (Source)', section: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_SUMMARY },
     // { value: 'createdSources.referrerHostname', label: 'Created Sources (Referrer Hostname)' },
     // { value: 'createdSources.referrerOriginPath', label: 'Created Sources (Referrer Origin Path)' },
     // { value: 'createdSources.referrerPath', label: 'Created Sources (Referrer Path)' },

     ...[{ value: 'createdSources.landingPageOriginPath', label: 'Landing Page' }].map((item) => ({
          ...item,
          section: SECTION_OPTION_SEGMENT.CREATED_LOCATION,
          sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO,
     })),

     ...CREATED_SOURCE_UTM_PROPERTY_OPTIONS.map((item) => ({ ...item, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...CREATED_SOURCE_CLICKID_PROPERTY_OPTIONS.map((item) => ({ ...item, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...CRM_CREATED_CHANNEL_EVENT_PROPERTY_OPTIONS,
];

// Ecommerce Event
const ECOMMERCE_PROPERTY_SEGMENT_OPTIONS = [
     ...[
          { value: 'eventID', label: 'Event ID' },
          { value: 'sessionID', label: 'Session ID' },
          { value: 'userID', label: 'User ID' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.IDENTIFIER_RELATIONSHIPS })),

     ...DATE_PROPERTY_OPTIONS,

     ...[
          { value: 'eCommerceValue', label: 'Total Value', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommercePurchaseRevenue', label: 'Purchase Revenue', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommerceShippingValue', label: 'Shipping Value', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'ecommerceCoupon', label: 'Coupon' },
          { value: 'ecommerceDiscount', label: 'Discount', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommerceTaxValue', label: 'Tax Value', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommerceCurrency', label: 'Currency' },
          { value: 'eCommerceTotalItemQuantity', label: 'Total Item Quantity', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommerceTransactionID', label: 'Transaction ID' },
          { value: 'eCommerceItemListID', label: 'Item List ID' },
          { value: 'eCommerceItemListName', label: 'Item List Name' },
          { value: 'eventLocationHostname', label: 'Event Location (Hostname)' },
          { value: 'eventLocationOriginPath', label: 'Event Location (Origin Path)' },
          { value: 'eventLocationURL', label: 'Event Location (URL)' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ECOMMERCE_EVENT_DETAILS })),

     ...[
          { value: 'eCommerceItems.itemID', label: 'Item ID' },
          { value: 'eCommerceItems.itemName', label: 'Item Name' },
          { value: 'eCommerceItems.itemBrand', label: 'Item Brand' },
          { value: 'eCommerceItems.itemVariants.item_variant', label: 'Item Variant' },
          { value: 'eCommerceItems.itemCategories.item_category', label: 'Item Category' },
          { value: 'eCommerceItems.itemCategories.item_category1', label: 'Item Category 1' },
          { value: 'eCommerceItems.itemCategories.item_category2', label: 'Item Category 2' },
          { value: 'eCommerceItems.itemCategories.item_category3', label: 'Item Category 3' },
          { value: 'eCommerceItems.itemCategories.item_category4', label: 'Item Category 4' },
          { value: 'eCommerceItems.itemPrice', label: 'Item Price', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommerceItems.itemQuantity', label: 'Item Quantity', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommerceItems.itemRevenue', label: 'Item Revenue', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommerceItems.coupon', label: 'Item Coupon' },
          { value: 'eCommerceItems.discount', label: 'Item Discount', fieldType: BIGQUERY_FIELD_TYPE.FLOAT },
          { value: 'eCommerceItems.itemListID', label: 'Item List ID' },
          { value: 'eCommerceItems.itemListName', label: 'Item List Name' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ECOMMERCE_ITEMS })),

     ...[
          { value: 'createdSessionIDFirst', label: 'session ID - first' },
          { value: 'createdSessionIDLast', label: 'session ID - last' },
          { value: 'createdSessionIDPath', label: 'session ID - path' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SESSION, sectionParent: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_INFO })),

     ...[
          { value: 'createdSourceFirst', label: 'userSource - first' },
          { value: 'createdSourceLast', label: 'userSource - last' },
          { value: 'createdSourcePath', label: 'userSource - path' },
          { value: 'createdSourceFirstND', label: 'non-direct userSource first' },
          { value: 'createdSessionIDLastND', label: 'non-direct userSource last' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_INFO })),

     // { value: 'createdSourceFirstND', label: 'Created Source (first non-direct)' },
     // { value: 'createdSessionIDFirstND', label: 'Created Session ID (first non-direct)' },
     // { value: 'createdSourceLastND', label: 'Created Source (last non-direct)' },
     // { value: 'createdSessionIDLastND', label: 'Created Session ID (last non-direct)' },
     // { value: 'createdSourcePathND', label: 'Created Source (path non-direct)' },
     // { value: 'createdSessionIDPathND', label: 'Created Session ID (path non-direct)' },
     // { value: 'createdSources.source', label: 'Created Sources (Source)', section: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_DETAILS },
     // { value: 'createdSources.timestampUTC', label: 'Created Sources (Source Timestamp UTC)' },
     // { value: 'createdSources.timestamp', label: 'Created Sources (Timestamp)', section: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_DETAILS },
     // { value: 'createdSources.sessionID', label: 'Created Sources <br>(Source Session ID)', section: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_DETAILS },
     // { value: 'createdSources.referrerHostname', label: 'Created Sources (Referrer Hostname)' },
     // { value: 'createdSources.referrerOriginPath', label: 'Created Sources (Referrer Origin Path)' },
     // { value: 'createdSources.referrerPath', label: 'Created Sources (Referrer Path)' },

     ...[
          { value: 'createdSources.referrerHostname', label: 'Referrer Hostname' },
          { value: 'createdSources.referrerOriginPath', label: 'Referrer Origin Path' },
          { value: 'createdSources.referrerPath', label: 'Referrer Path' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE_REFERRER, sectionParent: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_INFO })),

     ...[{ value: 'createdSources.landingPageOriginPath', label: 'Landing Page' }].map((item) => ({
          ...item,
          section: SECTION_OPTION_SEGMENT.CREATED_LOCATION,
          sectionParent: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_INFO,
     })),

     ...CREATED_SOURCE_UTM_PROPERTY_OPTIONS.map((item) => ({ ...item, sectionParent: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_INFO })),

     ...CREATED_SOURCE_CLICKID_PROPERTY_OPTIONS.map((item) => ({ ...item, sectionParent: SECTION_OPTION_SEGMENT.ECOMMERCE_SOURCE_INFO })),
];

// Sales Conversion
const SALES_CONVERSION_PROPERTY_SEGMENT_OPTIONS = [
     ...[
          { value: 'leadID', label: 'CRM Lead ID' },
          { value: 'contactID', label: 'CRM Contact ID' },
          { value: 'accountID', label: 'CRM Account ID' },
          { value: 'opportunityID', label: 'CRM Opportunity ID' },
          { value: 'quoteID', label: 'CRM Quote ID' },
          { value: 'orderID', label: 'CRM Order ID' },
          { value: 'contractID', label: 'CRM Contract ID' },
          { value: 'campaignID', label: 'CRM Campaign ID' },
          { value: 'activityEventID', label: 'CRM Activity Event ID' },
          { value: 'parentAccountID', label: 'CRM Parent Account ID' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.IDENTIFIER_RELATIONSHIPS })),

     ...DATE_PROPERTY_OPTIONS,

     ...[
          { value: 'conversionType', label: 'Conversion Type' },
          { value: 'conversionValue', label: 'Conversion Value' },
          { value: 'conversionCurrency', label: 'Conversion Currency' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CONVERSIONS })),

     ...[
          { value: 'createdSessionIDFirst', label: 'session ID - first' },
          { value: 'createdSessionIDLast', label: 'session ID - last' },
          { value: 'createdSessionIDPath', label: 'session ID - path' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SESSION, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...[
          { value: 'createdSourceFirst', label: 'userSource - first' },
          { value: 'createdSourceLast', label: 'userSource - last' },
          { value: 'createdSourcePath', label: 'userSource - path' },
          { value: 'createdSessionIDFirstND', label: 'non-direct userSource first' },
          { value: 'createdSourceLastND', label: 'non-direct userSource last' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SESSION, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...[
          { value: 'createdSources.referrerHostname', label: 'Referrer Hostname' },
          { value: 'createdSources.referrerOriginPath', label: 'Referrer Origin Path' },
          { value: 'createdSources.referrerPath', label: 'Referrer Path' },
     ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE_REFERRER, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...[{ value: 'createdSources.landingPageOriginPath', label: 'Landing Page' }].map((item) => ({
          ...item,
          section: SECTION_OPTION_SEGMENT.CREATED_LOCATION,
          sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO,
     })),

     ...CREATED_SOURCE_UTM_PROPERTY_OPTIONS.map((item) => ({ ...item, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...CREATED_SOURCE_CLICKID_PROPERTY_OPTIONS.map((item) => ({ ...item, sectionParent: SECTION_OPTION_SEGMENT.CONVERSION_SOURCE_INFO })),

     ...CRM_CREATED_CHANNEL_EVENT_PROPERTY_OPTIONS,
];

export const EVENT_CONDITION_FOCUS = [CONDITION_FOCUS.WEB_CONVERSION_EVENT, CONDITION_FOCUS.ECOMMERCE_EVENT, CONDITION_FOCUS.SALES_CONVERSION_EVENT];

export const MAPPING_CONDITION_FOCUS_PROPERTY_OPTION = {
     [CONDITION_FOCUS.WEB_CONVERSION_EVENT]: WEB_CONVERSION_PROPERTY_SEGMENT_OPTIONS,
     [CONDITION_FOCUS.ECOMMERCE_EVENT]: ECOMMERCE_PROPERTY_SEGMENT_OPTIONS,
     [CONDITION_FOCUS.SALES_CONVERSION_EVENT]: SALES_CONVERSION_PROPERTY_SEGMENT_OPTIONS,
};

export const SEGMENT_ALL_DATA_DEFAULT_NAME = 'All Data';

export const SEGMENT_BYTES_BILLED_THRESHOLD = 10;

export const SEGMENT_CHECKLIST_FIELDS = [
     'revenue',
     'currentCompanyRevenue',
     'employees',
     'industries',
     'primaryIndustry',
     'currentCompanyPrimaryIndustry',
     'companyState',
     'companyCountry',
     'dataSources',
     'channel',
     'channelFirst',
     'channelLast',
     'channelPath',
     'userSource',
     'userSourceFirst',
     'userSourceLast',
     'userSourcePath',
     'noneDirectSourcePath',
     'userSourceFirstPersonCreated',
     'userSourceLastPersonCreated',
     'userSourcePathPersonCreated',
     'identifiedSourceFirstND',
     'identifiedSourceLastND',
     'identifiedSourcePathND',
     'createdSourceFirst',
     'createdSourceLast',
     'createdSourcePath',
     'createdChannelFirst',
     'createdChannelLast',
     'createdChannelPath',
     'createdSourceChannel[first]',
     'createdSourceChannel[last]',
     'createdSourceChannel[path]',
     'createdSourceChannel[pathND]',
     'lifeTimeSourceFirst',
     'lifeTimeSourceLast',
     'lifeTimeSourcePath',
     'lifeTimeSourcePathND',
     'closedSourceChannel[last]',
     'closedSourceChannel[path]',
     'closedSourceChannel[pathND]',
     'closedSourceLast',
     'closedSourcePath',
     'closedSourcePathND',
     'skills',
     'targetAccountGroups'
];

export const INITIAL_SEGMENT_GROUP = {
     conjunction: '',
     mainOperator: 'include',
     // segmentObject: SEGMENT_OBJECT.EVENT,
     segmentObject: SEGMENT_OBJECT.SESSION,
     conditionFocus: CONDITION_FOCUS.PROPERTY,
     dateRangeFilter: {
          type: DATE_RANGE_FILTER_TYPE.LIFETIME_OF_THE_OBJECT,
          numberOfDaysPrevious: 0,
          startDate: '',
          endDate: '',
     },
     g0: {
          key: '',
          type: '',
          condition: '',
          value: '',
          property: '',
          conjunction: '',
     },
};

export const PREVIEW_OBJECT_ID_SEGMENT_TITLE = {
     [SEGMENT_OBJECT.EVENT]: 'Event ID',
     [SEGMENT_OBJECT.WEB_CONVERSION]: 'Conversion ID',
     [SEGMENT_OBJECT.SESSION]: 'Session ID',
     [SEGMENT_OBJECT.USER]: 'User ID',
     [SEGMENT_OBJECT.PEOPLE]: 'Person ID',
     [SEGMENT_OBJECT.COMPANY]: 'Company ID',
     [SEGMENT_OBJECT.CRM_LEAD]: 'CRM Lead ID',
     [SEGMENT_OBJECT.CRM_CONTACT]: 'CRM Contact ID',
     [SEGMENT_OBJECT.CRM_ACCOUNT]: 'CRM Account ID',
     [SEGMENT_OBJECT.CRM_OPPORTUNITY]: 'CRM Opportunity ID',
     [SEGMENT_OBJECT.CRM_QUOTE]: 'CRM Quote ID',
     [SEGMENT_OBJECT.CRM_ORDER]: 'CRM Order ID',
     [SEGMENT_OBJECT.CRM_CONTRACT]: 'CRM Contract ID',
     [SEGMENT_OBJECT.CRM_CAMPAIGN]: 'CRM Campaign ID',
     [SEGMENT_OBJECT.CRM_EVENT]: 'CRM Event ID',
};

export const UNRELATED_SEGMENT_OBJECT = {
     [SEGMENT_OBJECT.SESSION]: [SEGMENT_OBJECT.EVENT, SEGMENT_OBJECT.USER],
     [SEGMENT_OBJECT.CRM_LEAD]: [SEGMENT_OBJECT.CRM_QUOTE, SEGMENT_OBJECT.CRM_ORDER, SEGMENT_OBJECT.CRM_CONTRACT],
     [SEGMENT_OBJECT.CRM_ACCOUNT]: [SEGMENT_OBJECT.CRM_CAMPAIGN],
     [SEGMENT_OBJECT.CRM_QUOTE]: [SEGMENT_OBJECT.CRM_LEAD, SEGMENT_OBJECT.CRM_CAMPAIGN],
     [SEGMENT_OBJECT.CRM_ORDER]: [SEGMENT_OBJECT.CRM_LEAD, SEGMENT_OBJECT.CRM_CAMPAIGN],
     [SEGMENT_OBJECT.CRM_CONTRACT]: [SEGMENT_OBJECT.CRM_LEAD, SEGMENT_OBJECT.CRM_CAMPAIGN],
     [SEGMENT_OBJECT.CRM_CAMPAIGN]: [...LL_SEGMENT_OBJECTS, SEGMENT_OBJECT.CRM_QUOTE, SEGMENT_OBJECT.CRM_ORDER, SEGMENT_OBJECT.CRM_CONTRACT, SEGMENT_OBJECT.CRM_ACCOUNT],
};

export const DATE_PROPERTIES = ['date', 'hour', 'dayOfWeek', 'month', 'yearMonth', 'quarter', 'yearQuarter'];
